import {
  DragEvent,
  FormEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { supabase } from "./lib/supabase";
import {
  VehicleMediaPanel,
  type PendingVehicleDocument,
} from "./VehicleMediaPanel";
import { VehicleChronologyPanel } from "./VehicleChronologyPanel";
import { VehicleCommercialPanel } from "./VehicleCommercialPanel";
import { VehicleMarketingPanel } from "./VehicleMarketingPanel";
import { VehicleDocumentGenerator } from "./VehicleDocumentGenerator";
import { normalizeVehicleBrands } from "./vehicle-brands";
import { formatTaxIdentifier, normalizeEmail, validateCustomerIdentity, type CustomerKind } from "./validation";
import {
  formatCurrencyInput,
  parseCurrencyInputToCents,
} from "./currency-input";
import { CurrencyInput } from "./CurrencyInput";
import "./vehicle-stock.css";
import "./document-card-proportions.css";

type RoleKey = "stand_master" | "manager" | "sales" | "operations" | "user";
type VehicleProfileTab = "general" | "media" | "marketing" | "documents" | "chronology";
type Vehicle = {
  id: string;
  organization_id: string;
  stock_number: string | null;
  make: string;
  model: string;
  version: string | null;
  registration: string | null;
  vin: string | null;
  first_registration_year: number | null;
  first_registration_country_code: string | null;
  registration_date: string | null;
  fuel_type: string | null;
  transmission: string | null;
  mileage_km: number;
  color: string | null;
  paint_finish?: string | null;
  body_type: string | null;
  engine_capacity_cc: number | null;
  power_kw: number | null;
  doors: number | null;
  seats: number | null;
  purchase_date: string | null;
  supplier_name: string | null;
  stock_location: string | null;
  branch_id: string | null;
  warranty_months: number | null;
  expected_warranty_type: "own" | "acquired" | null;
  expected_warranty_product_id: string | null;
  internal_notes: string | null;
  record_status: "incomplete" | "complete";
  creation_status: "draft" | "active" | "discarded";
  purchase_price_cents: number | null;
  sale_price_cents: number | null;
  currency_code: string;
  stock_status: string;
  operational_stage: string;
  acquisition_type: string;
  publication_status: string;
  sold_publication_mode: string;
  pause_reason: string | null;
  featured: boolean;
  description: string | null;
  updated_at: string;
};
type StockBranch = { id: string; name: string; code: string; is_headquarters: boolean };
type ExpectedWarrantyProduct = { id: string; name: string; duration_months: number; provider_id: string; active: boolean };
type ExpectedWarrantyProvider = { id: string; name: string; active: boolean };
type ExpectedWarrantySettings = { mode: string; own_default_months: number } | null;
type VehicleBusinessTerms = {
  business_type: string;
  vat_regime: string;
  vat_rate_percent: number;
  vat_itemized: boolean;
  vat_deductible: boolean;
  available_for_sale_on: string | null;
  fixed_price: boolean;
  accepts_exchange: boolean;
  accepts_trade_in: boolean;
  price_excludes_isv: boolean;
  financing_available: boolean;
  iuc_amount_cents: number;
  estimated_vat_cents: number;
};
type ConsignmentOwner = {
  id: string;
  display_name: string;
  customer_type: string;
  legal_name: string | null;
  trade_name: string | null;
  customer_role: "buyer" | "owner" | "both";
  tax_identifier: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  notes: string | null;
};
type ConsignmentAgreement = {
  id: string;
  owner_id: string;
  status: string;
  owner_amount_cents: number;
  commission_type: string;
  commission_value: number | null;
  minimum_sale_price_cents: number | null;
  starts_on: string;
  ends_on: string | null;
  terms: string | null;
  internal_notes: string | null;
};
type VehicleReservation = {
  id: string;
  vehicle_id?: string;
  customer_name: string;
  customer_email: string | null;
  customer_phone: string | null;
  assigned_user_id: string;
  status: string;
  expires_at: string;
  deposit_cents: number | null;
  currency_code: string;
  notes: string | null;
};
type ReservationRequest = {
  id: string;
  requested_by: string;
  proposed_customer_name: string;
  message: string;
  status: string;
  created_at: string;
};
type VehicleEvent = {
  id: number;
  event_type: string;
  before_data: Record<string, unknown> | null;
  after_data: Record<string, unknown> | null;
  created_at: string;
};
type VehicleSpecification = {
  gears: number | null;
  traction: string | null;
  co2_g_km: number | null;
  previous_owners: number | null;
  inspection_until: string | null;
  factory_warranty_until: string | null;
  factory_warranty_km: number | null;
  full_service_history: boolean;
  second_key: boolean;
  non_smoker: boolean;
  classic_vehicle: boolean;
  last_service_on: string | null;
  last_service_km: number | null;
  service_interval_months: number | null;
  service_interval_km: number | null;
  timing_belt_on: string | null;
  timing_belt_km: number | null;
  battery_capacity_kwh: number | null;
  battery_health_percent: number | null;
  electric_range_km: number | null;
  consumption_kwh_100km: number | null;
  charging_connector: string | null;
  max_charge_kw: number | null;
};
type EquipmentItem = {
  id: string;
  organization_id: string | null;
  category: string;
  name: string;
  sort_order: number;
};
const equipmentCategoryLabels: Record<string, string> = {
  audio_multimedia: "Áudio e multimédia",
  comfort: "Conforto",
  technology: "Tecnologia",
  performance: "Desempenho",
  safety: "Segurança",
};
type VehicleDeletion = {
  id: number;
  stock_number: string | null;
  registration: string | null;
  make: string;
  model: string;
  reason: string;
  deleted_at: string;
};
const stockLabels: Record<string, string> = {
  in_stock: "Em stock",
  reserved: "Reservada",
  sale_in_progress: "Em venda",
  sold: "Vendida",
  delivery: "A entregar",
  delivered: "Entregue",
  paused: "Em pausa",
  archived: "Arquivada",
};
const stageLabels: Record<string, string> = {
  purchase: "Compra",
  transport: "Transporte",
  reception: "Receção",
  checkup: "Verificação",
  preparation: "Preparação",
  workshop: "Oficina",
  inspection: "Inspeção",
  exposure: "Exposição",
};
const acquisitionLabels: Record<string, string> = {
  owned: "Viatura própria",
  consignment: "À consignação",
  trade_in: "Retoma",
};
const publicationLabels: Record<string, string> = {
  draft: "Rascunho",
  published: "Publicada",
  unpublished: "Não publicada",
};
const vehicleColors = [
  "Branco", "Preto", "Cinzento", "Prateado", "Azul", "Vermelho",
  "Verde", "Amarelo", "Laranja", "Castanho", "Bege", "Dourado",
  "Bronze", "Roxo", "Rosa", "Outra",
];
const paintFinishLabels: Record<string, string> = {
  solid: "Sólida",
  metallic: "Metálica",
  pearl: "Perolizada",
  matte: "Mate",
  satin: "Acetinada",
  two_tone: "Bicolor",
};
const vatRegimeHelp: Record<string, string> = {
  margin_scheme: "Usado quando o IVA é calculado apenas sobre a margem positiva entre a venda e a compra.",
  standard_vat: "O sistema estima o IVA incluído na venda e desconta o IVA da compra quando este for legalmente dedutível.",
  vat_exempt: "Não é estimado IVA para esta operação. Confirme que existe fundamento legal para a isenção.",
  intra_community: "Aquisição ou venda dentro da União Europeia. Custos fiscais adicionais devem ser lançados no Financeiro.",
  import: "Viatura importada de fora do mercado nacional. Alfândega, ISV e outros encargos devem ser registados no Financeiro.",
  other: "Situação fiscal especial. O sistema não presume o imposto; registe os custos confirmados no Financeiro.",
};
const splitVehicleColor = (value?: string | null) => {
  const color = value?.trim() ?? "";
  const match = color.match(/^(.*?) · (Sólida|Metálica|Perolizada|Mate|Acetinada|Bicolor)$/);
  if (!match) return { color, paintFinish: "" };
  const paintFinish = Object.entries(paintFinishLabels).find(([, label]) => label === match[2])?.[0] ?? "";
  return { color: match[1], paintFinish };
};
export const formatPortugueseRegistration = (value: string) => {
  const characters = value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 6);
  if (characters.length < 2) return characters;
  if (characters.length === 2) return `${characters}-`;
  if (characters.length < 4)
    return `${characters.slice(0, 2)}-${characters.slice(2)}`;
  if (characters.length === 4)
    return `${characters.slice(0, 2)}-${characters.slice(2)}-`;
  return `${characters.slice(0, 2)}-${characters.slice(2, 4)}-${characters.slice(4)}`;
};
const bodyTypes = [
  "Berlina",
  "Carrinha",
  "Citadino",
  "Coupé",
  "Cabrio",
  "Monovolume",
  "SUV / Todo-o-terreno",
  "Pick-up",
  "Comercial",
  "Outro",
];
const saleStatuses = new Set([
  "sale_in_progress",
  "sold",
  "delivery",
  "delivered",
]);
const money = (value: number | null, currency: string) =>
  value == null
    ? "—"
    : new Intl.NumberFormat(currency === "BRL" ? "pt-BR" : "pt-PT", {
        style: "currency",
        currency,
      }).format(value / 100);

function VehiclePublicationDialog({
  vehicle,
  organizationId,
  onClose,
  onSaved,
  onEditVehicle,
  onOpenSiteSettings,
}: {
  vehicle: Vehicle;
  organizationId: string;
  onClose: () => void;
  onSaved: (message: string) => void | Promise<void>;
  onEditVehicle: (tab: "general" | "media") => void;
  onOpenSiteSettings: () => void;
}) {
  const [siteReady, setSiteReady] = useState(false);
  const [photoReady, setPhotoReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [publishOnSite, setPublishOnSite] = useState(
    vehicle.publication_status === "published",
  );

  useEffect(() => {
    void (async () => {
      const [siteResult, photoResult] = await Promise.all([
        supabase.from("organization_sites").select("enabled,published_revision_id").eq("organization_id", organizationId).maybeSingle(),
        supabase.from("vehicle_media").select("id", { count: "exact", head: true }).eq("vehicle_id", vehicle.id).eq("status", "ready").not("public_path", "is", null),
      ]);
      setLoading(false);
      if (siteResult.error || photoResult.error) setError(siteResult.error?.message ?? photoResult.error?.message ?? "Não foi possível verificar a publicação.");
      setSiteReady(Boolean(siteResult.data?.enabled && siteResult.data?.published_revision_id));
      setPhotoReady((photoResult.count ?? 0) > 0);
    })();
  }, [organizationId, vehicle.id]);

  const issues = [
    ...(vehicle.creation_status !== "active" ? [{ key: "creation", title: "Cadastro da viatura por concluir", detail: "Conclua a criação da viatura antes de a publicar.", action: "Ficha da viatura", run: () => onEditVehicle("general") }] : []),
    ...(vehicle.record_status !== "complete" ? [{ key: "record", title: "Ficha marcada como incompleta", detail: "Abra a ficha, confirme os campos obrigatórios e guarde novamente.", action: "Rever ficha", run: () => onEditVehicle("general") }] : []),
    ...(!vehicle.registration?.trim() ? [{ key: "registration", title: "Matrícula em falta", detail: "Indique a matrícula na identificação da viatura.", action: "Preencher matrícula", run: () => onEditVehicle("general") }] : []),
    ...(!vehicle.first_registration_year ? [{ key: "year", title: "Ano em falta", detail: "Indique o ano da primeira matrícula.", action: "Preencher ano", run: () => onEditVehicle("general") }] : []),
    ...(!vehicle.sale_price_cents || vehicle.sale_price_cents <= 0 ? [{ key: "price", title: "Preço de venda em falta", detail: "O anúncio precisa de um preço de venda superior a zero.", action: "Indicar preço", run: () => onEditVehicle("general") }] : []),
    ...(!photoReady ? [{ key: "photo", title: "Fotografia pública em falta", detail: "Carregue pelo menos uma fotografia pronta para o anúncio.", action: "Adicionar fotografia", run: () => onEditVehicle("media") }] : []),
    ...(vehicle.stock_status === "archived" ? [{ key: "archived", title: "Viatura arquivada", detail: "Reative a viatura antes de publicar.", action: "Abrir ficha", run: () => onEditVehicle("general") }] : []),
    ...(!siteReady ? [{ key: "site", title: "Site do stand não publicado", detail: "Escolha e publique o site comercial do stand antes de enviar viaturas.", action: "Configurar site", run: onOpenSiteSettings }] : []),
  ];
  const canPublish = !loading && issues.length === 0;

  const save = async () => {
    if (publishOnSite && !canPublish) {
      setError("Corrija os impedimentos indicados antes de publicar a viatura.");
      return;
    }
    setSaving(true);
    setError(null);
    const nextStatus = publishOnSite ? "published" : "unpublished";
    const { data, error } = await supabase
      .from("vehicles")
      .update({ publication_status: nextStatus })
      .eq("id", vehicle.id)
      .eq("organization_id", organizationId)
      .eq("record_status", "complete")
      .eq("creation_status", "active")
      .select("id,publication_status")
      .maybeSingle();
    setSaving(false);
    if (error) {
      setError(error.message);
      return;
    }
    if (!data || data.publication_status !== nextStatus) {
      setError("O estado não foi alterado. Reveja abaixo o que ainda impede a publicação.");
      return;
    }
    await onSaved(
      publishOnSite
        ? "Viatura publicada no site comercial do stand."
        : "Viatura retirada do site comercial do stand.",
    );
  };

  return (
    <div className="stock-dialog-backdrop">
      <section className="stock-dialog publication-dialog" role="dialog" aria-modal="true" aria-label="Publicar viatura">
        <header>
          <div>
            <p className="eyebrow">Publicação da viatura</p>
            <h2>{vehicle.make} {vehicle.model}</h2>
            <p>Escolha os destinos realmente configurados neste stand.</p>
          </div>
          <button type="button" className="publication-close" aria-label="Fechar" onClick={onClose}>×</button>
        </header>
        <div className="publication-destinations">
          {loading ? (
            <p>A verificar os canais configurados…</p>
          ) : (
            <label className={canPublish ? "available" : "unavailable"}>
              <input
                type="checkbox"
                checked={publishOnSite}
                onChange={(event) => setPublishOnSite(event.target.checked)}
                disabled={!canPublish}
              />
              <span>
                <strong>Site comercial do stand</strong>
                <small>{canPublish ? "Pronta para publicar" : `${issues.length} impedimento${issues.length === 1 ? "" : "s"} por corrigir`}</small>
              </span>
            </label>
          )}
          {!loading && issues.length > 0 && (
            <section className="publication-readiness" aria-label="Impedimentos da publicação">
              <header><strong>O que impede a publicação</strong><span>{issues.length} por resolver</span></header>
              {issues.map((issue) => (
                <article key={issue.key}>
                  <div><strong>{issue.title}</strong><small>{issue.detail}</small></div>
                  <button type="button" className="stock-secondary" onClick={issue.run}>{issue.action}</button>
                </article>
              ))}
            </section>
          )}
          <p className="publication-integrations-note">
            Os portais externos só serão apresentados aqui quando existir uma ligação oficial, configurada e validada. Simuladores não são mostrados como canais reais.
          </p>
        </div>
        {error && <p className="stock-error">{error}</p>}
        <footer>
          <button type="button" className="stock-secondary" onClick={onClose}>Cancelar</button>
          <button type="button" className="stock-primary" onClick={() => void save()} disabled={saving || loading || (publishOnSite && !canPublish)}>Guardar</button>
        </footer>
      </section>
    </div>
  );
}

export function VehicleStock({
  organizationId,
  countryCode,
  currencyCode,
  writable,
  roleKey,
  canManageLifecycle,
  canManageFinance,
  vehicleLimit,
  circuit,
  onOpenSiteSettings,
}: {
  organizationId: string;
  countryCode: string;
  currencyCode: string;
  writable: boolean;
  roleKey: RoleKey;
  canManageLifecycle: boolean;
  canManageFinance: boolean;
  vehicleLimit: number | null;
  circuit: "inventory" | "workflow" | "sold" | "archived";
  onOpenSiteSettings: () => void;
}) {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]),
    [deletions, setDeletions] = useState<VehicleDeletion[]>([]),
    [loading, setLoading] = useState(true),
    [selected, setSelected] = useState<Vehicle | null>(null),
    [selectedInitialTab, setSelectedInitialTab] = useState<VehicleProfileTab>("general"),
    [creating, setCreating] = useState(false),
    [status, setStatus] = useState("all"),
    [search, setSearch] = useState(""),
    [useStockReference, setUseStockReference] = useState(false),
    [stockReferenceRequired, setStockReferenceRequired] = useState(false),
    [vehicleBrands, setVehicleBrands] = useState<string[]>(normalizeVehicleBrands(null)),
    [maxVehiclePhotos, setMaxVehiclePhotos] = useState(8),
    [vehicleThumbnails, setVehicleThumbnails] = useState<Record<string, string>>({}),
    [publicationPhotos, setPublicationPhotos] = useState<Record<string, boolean>>({}),
    [publicationSiteReady, setPublicationSiteReady] = useState(false),
    [activeReservations, setActiveReservations] = useState<Record<string, VehicleReservation>>({}),
    [reservationVehicle, setReservationVehicle] = useState<Vehicle | null>(null),
    [publicationVehicle, setPublicationVehicle] = useState<Vehicle | null>(null),
    [documentVehicle, setDocumentVehicle] = useState<Vehicle | null>(null),
    [draggedVehicleId, setDraggedVehicleId] = useState<string | null>(null),
    [stageSavingId, setStageSavingId] = useState<string | null>(null),
    [expandedCircuitStage, setExpandedCircuitStage] = useState<string | null>(null),
    [circuitSelectedIds, setCircuitSelectedIds] = useState<string[]>([]),
    [circuitQuery, setCircuitQuery] = useState(""),
    [circuitMake, setCircuitMake] = useState("all"),
    [circuitModel, setCircuitModel] = useState("all"),
    [circuitYear, setCircuitYear] = useState("all"),
    [circuitSort, setCircuitSort] = useState("name"),
    [circuitBatchTarget, setCircuitBatchTarget] = useState("preparation"),
    [circuitBatchSaving, setCircuitBatchSaving] = useState(false),
    [stockCircuit, setStockCircuit] = useState<"inventory" | "workflow" | "sold" | "archived">(circuit),
    [archiveAcquisition, setArchiveAcquisition] = useState("all"),
    [archiveYear, setArchiveYear] = useState("all"),
    [message, setMessage] = useState<string | null>(null);
  const load = async () => {
    setLoading(true);
    const [vehicleResult, deletionResult, settingsResult, mediaSettingsResult, reservationResult, siteResult] = await Promise.all([
      supabase
        .from("vehicles")
        .select("*")
        .eq("organization_id", organizationId)
        .order("updated_at", { ascending: false }),
      canManageLifecycle
        ? supabase
            .from("vehicle_deletion_audit")
            .select("id,stock_number,registration,make,model,reason,deleted_at")
            .eq("organization_id", organizationId)
            .order("deleted_at", { ascending: false })
            .limit(5)
        : Promise.resolve({ data: [], error: null }),
      supabase
        .from("organization_settings")
        .select("settings")
        .eq("organization_id", organizationId)
        .single(),
      supabase
        .from("organization_media_settings")
        .select("max_vehicle_photos")
        .eq("organization_id", organizationId)
        .single(),
      supabase
        .from("vehicle_reservations")
        .select("id,vehicle_id,customer_name,customer_email,customer_phone,assigned_user_id,status,expires_at,deposit_cents,currency_code,notes")
        .eq("organization_id", organizationId)
        .eq("status", "active"),
      supabase.from("organization_sites").select("enabled,published_revision_id").eq("organization_id", organizationId).maybeSingle(),
    ]);
    if (vehicleResult.error) setMessage(vehicleResult.error.message);
    if (deletionResult.error) setMessage(deletionResult.error.message);
    if (settingsResult.error) setMessage(settingsResult.error.message);
    if (mediaSettingsResult.error) setMessage(mediaSettingsResult.error.message);
    if (reservationResult.error) setMessage(reservationResult.error.message);
    if (siteResult.error) setMessage(siteResult.error.message);
    setPublicationSiteReady(Boolean(siteResult.data?.enabled && siteResult.data?.published_revision_id));
    const standSettings = (
      settingsResult.data?.settings as
        | {
            stand?: {
              use_stock_reference?: boolean;
              stock_reference_required?: boolean;
              vehicle_brands?: string[];
            };
          }
        | undefined
    )?.stand;
    setUseStockReference(standSettings?.use_stock_reference === true);
    setStockReferenceRequired(
      standSettings?.use_stock_reference === true &&
        standSettings?.stock_reference_required === true,
    );
    setVehicleBrands(normalizeVehicleBrands(standSettings?.vehicle_brands));
    setMaxVehiclePhotos(mediaSettingsResult.data?.max_vehicle_photos ?? 8);
    setActiveReservations(Object.fromEntries(((reservationResult.data ?? []) as VehicleReservation[]).map((item) => [item.vehicle_id!, item])));
    const loadedVehicles = (vehicleResult.data ?? []) as Vehicle[];
    const thumbnailUrls: Record<string, string> = {};
    const readyPublicationPhotos: Record<string, boolean> = {};
    if (loadedVehicles.length) {
      const mediaResult = await supabase
        .from("vehicle_media")
        .select("id,vehicle_id,original_path,public_path,is_primary,position,status")
        .in("vehicle_id", loadedVehicles.map((vehicle) => vehicle.id))
        .neq("status", "removed")
        .order("is_primary", { ascending: false })
        .order("position");
      if (mediaResult.error) setMessage(mediaResult.error.message);
      const selectedMedia = new Map<string, { id: string; vehicle_id: string; original_path: string; public_path: string | null }>();
      for (const item of mediaResult.data ?? []) {
        if (!selectedMedia.has(item.vehicle_id)) selectedMedia.set(item.vehicle_id, item);
        if (item.status === "ready" && item.public_path) readyPublicationPhotos[item.vehicle_id] = true;
      }
      for (const item of selectedMedia.values()) {
        if (item.public_path) {
          thumbnailUrls[item.vehicle_id] = supabase.storage
            .from("vehicle-media-public")
            .getPublicUrl(item.public_path).data.publicUrl;
        } else {
          const signed = await supabase.storage
            .from("vehicle-media-originals")
            .createSignedUrl(item.original_path, 600, {
              transform: { width: 240, height: 160, resize: "cover", quality: 72 },
            });
          if (signed.data) thumbnailUrls[item.vehicle_id] = signed.data.signedUrl;
        }
      }
    }
    setVehicleThumbnails(thumbnailUrls);
    setPublicationPhotos(readyPublicationPhotos);
    setVehicles(loadedVehicles);
    setDeletions((deletionResult.data ?? []) as VehicleDeletion[]);
    setLoading(false);
  };
  useEffect(() => {
    void load();
  }, [organizationId]);
  useEffect(() => {
    setStockCircuit(circuit);
    setStatus("all");
    setSearch("");
    setArchiveAcquisition("all");
    setArchiveYear("all");
    setExpandedCircuitStage(null);
  }, [circuit]);
  const refreshMediaSettings = async () => {
    const { data, error } = await supabase
      .from("organization_media_settings")
      .select("max_vehicle_photos")
      .eq("organization_id", organizationId)
      .single();
    if (error) {
      setMessage(error.message);
      return;
    }
    setMaxVehiclePhotos(data?.max_vehicle_photos ?? 8);
  };
  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => setMessage(null), 10000);
    return () => clearTimeout(timer);
  }, [message]);
  const filtered = useMemo(
    () =>
      vehicles.filter(
        (v) =>
          (stockCircuit === "archived"
            ? v.stock_status === "archived"
            : stockCircuit === "sold"
            ? ["sold", "delivery", "delivered"].includes(v.stock_status)
            : stockCircuit === "workflow"
              ? !["sold", "delivery", "delivered", "archived"].includes(v.stock_status)
              : !["sold", "delivery", "delivered", "archived"].includes(v.stock_status)) &&
          (status === "all" ||
            (status === "creation_draft"
              ? v.creation_status === "draft"
              : v.stock_status === status)) &&
          (stockCircuit !== "archived" || archiveAcquisition === "all" || v.acquisition_type === archiveAcquisition) &&
          (stockCircuit !== "archived" || archiveYear === "all" || String(v.first_registration_year ?? "") === archiveYear) &&
          `${v.make} ${v.model} ${v.version ?? ""} ${v.registration ?? ""} ${v.stock_number ?? ""}`
            .toLowerCase()
            .includes(search.toLowerCase()),
      ),
    [vehicles, status, search, stockCircuit, archiveAcquisition, archiveYear],
  );
  const publicationBlocked = (vehicle: Vehicle) =>
    vehicle.publication_status !== "published" &&
    (vehicle.creation_status !== "active" || vehicle.record_status !== "complete" ||
      !vehicle.registration?.trim() || !vehicle.first_registration_year ||
      !vehicle.sale_price_cents || vehicle.sale_price_cents <= 0 ||
      !publicationPhotos[vehicle.id] || vehicle.stock_status === "archived" ||
      !publicationSiteReady);
  const openVehicleProfile = (vehicle: Vehicle, tab: VehicleProfileTab = "general") => {
    setSelectedInitialTab(tab);
    setSelected(vehicle);
  };
  const moveVehicleStage = async (vehicle: Vehicle, nextStage: string) => {
    if (!writable || vehicle.operational_stage === nextStage || stageSavingId) return;
    setStageSavingId(vehicle.id);
    setMessage(null);
    const previousStage = vehicle.operational_stage;
    setVehicles((current) => current.map((item) => item.id === vehicle.id ? { ...item, operational_stage: nextStage } : item));
    const { data: authData } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("vehicles")
      .update({ operational_stage: nextStage, updated_by: authData.user?.id ?? null })
      .eq("id", vehicle.id)
      .eq("organization_id", organizationId);
    if (error) {
      setVehicles((current) => current.map((item) => item.id === vehicle.id ? { ...item, operational_stage: previousStage } : item));
      setMessage(`Não foi possível mover a viatura: ${error.message}`);
    } else {
      setMessage(`${vehicle.make} ${vehicle.model} movida para ${stageLabels[nextStage]}.`);
    }
    setStageSavingId(null);
  };
  const moveSelectedCircuitVehicles = async () => {
    const selectedVehicles = vehicles.filter((vehicle) => circuitSelectedIds.includes(vehicle.id) && vehicle.operational_stage !== circuitBatchTarget);
    if (!writable || !selectedVehicles.length || circuitBatchSaving) return;
    setCircuitBatchSaving(true);
    setMessage(null);
    const { data: authData } = await supabase.auth.getUser();
    const { error } = await supabase
      .from("vehicles")
      .update({ operational_stage: circuitBatchTarget, updated_by: authData.user?.id ?? null })
      .in("id", selectedVehicles.map((vehicle) => vehicle.id))
      .eq("organization_id", organizationId);
    if (error) {
      setMessage(`Não foi possível mover as viaturas: ${error.message}`);
    } else {
      setVehicles((current) => current.map((vehicle) => circuitSelectedIds.includes(vehicle.id) ? { ...vehicle, operational_stage: circuitBatchTarget } : vehicle));
      setMessage(`${selectedVehicles.length} ${selectedVehicles.length === 1 ? "viatura movida" : "viaturas movidas"} para ${stageLabels[circuitBatchTarget]}.`);
      setCircuitSelectedIds([]);
      setExpandedCircuitStage(null);
    }
    setCircuitBatchSaving(false);
  };
  const circuitMakes = [...new Set(filtered.map((vehicle) => vehicle.make).filter(Boolean))].sort((a, b) => a.localeCompare(b, "pt"));
  const circuitModels = [...new Set(filtered.filter((vehicle) => circuitMake === "all" || vehicle.make === circuitMake).map((vehicle) => vehicle.model).filter(Boolean))].sort((a, b) => a.localeCompare(b, "pt"));
  const circuitYears = [...new Set(filtered.map((vehicle) => vehicle.first_registration_year).filter((year): year is number => Boolean(year)))].sort((a, b) => b - a);
  const sortCircuitVehicles = (items: Vehicle[]) => [...items].sort((a, b) => {
    if (circuitSort === "year_desc") return (b.first_registration_year ?? 0) - (a.first_registration_year ?? 0);
    if (circuitSort === "year_asc") return (a.first_registration_year ?? 0) - (b.first_registration_year ?? 0);
    if (circuitSort === "recent") return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    return `${a.make} ${a.model}`.localeCompare(`${b.make} ${b.model}`, "pt", { sensitivity: "base" });
  });
  const expandedCircuitVehicles = sortCircuitVehicles(filtered.filter((vehicle) =>
    vehicle.operational_stage === expandedCircuitStage &&
    (circuitMake === "all" || vehicle.make === circuitMake) &&
    (circuitModel === "all" || vehicle.model === circuitModel) &&
    (circuitYear === "all" || String(vehicle.first_registration_year ?? "") === circuitYear) &&
    `${vehicle.make} ${vehicle.model} ${vehicle.version ?? ""} ${vehicle.registration ?? ""} ${vehicle.stock_number ?? ""}`.toLowerCase().includes(circuitQuery.toLowerCase()),
  ));
  const counts = useMemo(
    () => ({
      total: vehicles.filter(
        (v) => v.creation_status === "active" && !["sold", "delivery", "delivered", "archived"].includes(v.stock_status),
      ).length,
      drafts: vehicles.filter((v) => v.creation_status === "draft").length,
      published: vehicles.filter(
        (v) =>
          v.creation_status === "active" &&
          !["sold", "delivery", "delivered", "archived"].includes(v.stock_status) &&
          v.publication_status === "published",
      ).length,
      preparation: vehicles.filter(
        (v) =>
          v.creation_status === "active" &&
          !["sold", "delivery", "delivered", "archived"].includes(v.stock_status) &&
          ["preparation", "workshop", "inspection"].includes(
            v.operational_stage,
          ),
      ).length,
      sold: vehicles.filter(
        (v) =>
          v.creation_status === "active" &&
          ["sold", "delivery", "delivered"].includes(v.stock_status),
      ).length,
      awaitingDelivery: vehicles.filter((v) => v.creation_status === "active" && v.stock_status === "delivery").length,
      delivered: vehicles.filter((v) => v.creation_status === "active" && v.stock_status === "delivered").length,
      reserved: vehicles.filter((v) => v.creation_status === "active" && v.stock_status === "reserved").length,
      saleInProgress: vehicles.filter((v) => v.creation_status === "active" && v.stock_status === "sale_in_progress").length,
      archived: vehicles.filter((v) => v.stock_status === "archived").length,
    }),
    [vehicles],
  );
  const limitReached = vehicleLimit !== null && counts.total >= vehicleLimit;
  return (
    <section className="vehicle-stock">
      <header className="stock-heading">
        <div>
          <p className="eyebrow">Gestão de viaturas</p>
          <h2>{stockCircuit === "archived" ? "Viaturas arquivadas" : stockCircuit === "sold" ? "Viaturas vendidas" : stockCircuit === "workflow" ? "Circuito de veículos" : "Stock"}</h2>
          <p>
            {stockCircuit === "archived"
              ? "Consulte viaturas retiradas do stock, preserve o histórico e reative-as quando necessário."
              : stockCircuit === "sold"
              ? "Consulte o percurso pós-venda sem ocupar as vagas ativas do stock."
              : stockCircuit === "workflow"
                ? "Acompanhe cada viatura desde a entrada até à entrega, sem perder o respetivo perfil."
                : "Estado comercial, etapa operacional e publicação são controlados separadamente."}
          </p>
        </div>
        <div className="stock-heading-actions">
          {vehicleLimit !== null && stockCircuit === "inventory" && (
            <div className={`plan-usage compact ${limitReached ? "reached" : ""}`} title={`${vehicleLimit - counts.total} vagas disponíveis no plano contratado.`}>
              <strong>{counts.total}/{vehicleLimit}</strong>
              <span>viaturas ativas</span>
            </div>
          )}
          {writable && stockCircuit === "inventory" && (
            <button className="stock-primary" onClick={async () => { await refreshMediaSettings(); setCreating(true); }}>
              + Adicionar viatura
            </button>
          )}
        </div>
      </header>
      {message && (
        <button className="stock-notice" onClick={() => setMessage(null)}>
          {message} ×
        </button>
      )}
      {stockCircuit === "archived" ? <div className="stock-summary archived-summary">
        <article><small>Arquivo</small><strong>{counts.archived}</strong></article>
        <article><small>Resultado dos filtros</small><strong>{filtered.length}</strong></article>
      </div> : stockCircuit === "inventory" ? <div className="stock-summary stock-summary-inventory">
        <article><small>Stock atual</small><strong>{counts.total}</strong></article>
        <article><small>Publicadas</small><strong>{counts.published}</strong></article>
        <article><small>Em preparação</small><strong>{counts.preparation}</strong></article>
        {canManageLifecycle && <article><small>Cadastros por concluir</small><strong>{counts.drafts}</strong></article>}
      </div> : stockCircuit === "workflow" ? <div className="stock-summary vehicle-circuit-summary">
        <article><small>Inventário ativo</small><strong>{counts.total}</strong></article>
        <article><small>Em preparação</small><strong>{counts.preparation}</strong></article>
        <article><small>Reservadas</small><strong>{counts.reserved}</strong></article>
        <article><small>Venda em curso</small><strong>{counts.saleInProgress}</strong></article>
        <article><small>Pós-venda</small><strong>{counts.sold}</strong></article>
      </div> : <div className="stock-summary sold-summary">
        <article><small>Histórico de vendas</small><strong>{counts.sold}</strong></article>
        <article><small>A entregar</small><strong>{counts.awaitingDelivery}</strong></article>
        <article><small>Entregues</small><strong>{counts.delivered}</strong></article>
      </div>}
      {stockCircuit === "sold" && <div className="sold-circuit-help"><strong>Circuito pós-venda</strong><span>Vendida → A entregar → Entregue. A viatura permanece no histórico, fora das vagas ativas do stock.</span></div>}
      {stockCircuit === "archived" && <div className="sold-circuit-help"><strong>Arquivo recuperável</strong><span>As viaturas arquivadas não ocupam vagas nem permanecem publicadas. Abra a ficha para consultar o histórico ou reativar a viatura.</span></div>}
      <div className={`stock-filters ${stockCircuit === "archived" ? "archived-filters" : ""}`}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Pesquisar viatura, matrícula ou referência…"
        />
        {stockCircuit === "archived" ? <>
          <select value={archiveAcquisition} onChange={(e) => setArchiveAcquisition(e.target.value)} aria-label="Filtrar por tipo de entrada">
            <option value="all">Todos os tipos de entrada</option>
            {Object.entries(acquisitionLabels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
          <select value={archiveYear} onChange={(e) => setArchiveYear(e.target.value)} aria-label="Filtrar por ano">
            <option value="all">Todos os anos</option>
            {[...new Set(vehicles.filter((v) => v.stock_status === "archived" && v.first_registration_year).map((v) => String(v.first_registration_year)))].sort((a, b) => Number(b) - Number(a)).map((year) => <option key={year} value={year}>{year}</option>)}
          </select>
        </> : <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="all">Todos os estados</option>
          {canManageLifecycle && (
            <option value="creation_draft">Cadastros por concluir</option>
          )}
          {Object.entries(stockLabels).filter(([key]) => stockCircuit === "sold" ? ["sold", "delivery", "delivered"].includes(key) : stockCircuit === "workflow" ? key !== "archived" : !["sold", "delivery", "delivered", "archived"].includes(key)).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>}
      </div>
      {loading ? (
        <div className="stock-empty">A carregar o stock…</div>
      ) : filtered.length === 0 ? (
        <div className="stock-empty">
          <strong>Não existem viaturas para estes filtros.</strong>
          <span>
            {writable
              ? "Adicione uma viatura ou altere os filtros."
              : "O stand ainda não registou viaturas."}
          </span>
        </div>
      ) : stockCircuit === "workflow" ? (
        <>
        <div className="vehicle-circuit-board" aria-label="Circuito operacional das viaturas">
          {Object.entries(stageLabels).map(([stage, label]) => {
            const stageVehicles = sortCircuitVehicles(filtered.filter((vehicle) => vehicle.operational_stage === stage));
            const visibleStageVehicles = stageVehicles.slice(0, 4);
            return <section key={stage} className={`vehicle-circuit-column ${draggedVehicleId ? "is-dragging" : ""}`} onDragOver={(event) => { if (writable) event.preventDefault(); }} onDrop={(event) => {
              event.preventDefault();
              const vehicle = filtered.find((item) => item.id === draggedVehicleId);
              setDraggedVehicleId(null);
              if (vehicle) void moveVehicleStage(vehicle, stage);
            }}>
              <header><span>{label}</span><div><small>{Math.min(visibleStageVehicles.length, stageVehicles.length)} de {stageVehicles.length}</small><button type="button" onClick={() => { setExpandedCircuitStage(stage); setCircuitQuery(""); setCircuitMake("all"); setCircuitModel("all"); setCircuitYear("all"); }}>Expandir</button></div></header>
              <div className="vehicle-circuit-cards">
                {stageVehicles.length === 0 && <div className="vehicle-circuit-empty">Arraste uma viatura para esta etapa</div>}
                {visibleStageVehicles.map((v) => (
                  <article key={v.id} className={`vehicle-circuit-card ${stageSavingId === v.id ? "is-saving" : ""}`} draggable={writable && stageSavingId !== v.id} onDragStart={(event) => {
                    setDraggedVehicleId(v.id);
                    event.dataTransfer.effectAllowed = "move";
                    event.dataTransfer.setData("text/plain", v.id);
                  }} onDragEnd={() => setDraggedVehicleId(null)}>
                    <div className="vehicle-circuit-card-heading">
                      <button type="button" className="vehicle-circuit-identity" onClick={() => openVehicleProfile(v)}>
                        <span className="vehicle-circuit-photo">{vehicleThumbnails[v.id] ? <img src={vehicleThumbnails[v.id]} alt="" /> : <span>Sem foto</span>}</span>
                        <span><strong>{v.make} {v.model}</strong><small>{v.registration ?? v.stock_number ?? "Sem referência"}</small></span>
                      </button>
                      <details className="vehicle-circuit-menu">
                        <summary aria-label={`Ações de ${v.make} ${v.model}`} title="Ações da viatura">⋮</summary>
                        <div>
                          <button onClick={() => openVehicleProfile(v)}>Editar viatura</button>
                          <button onClick={() => openVehicleProfile(v, "media")}>Fotografias</button>
                          <button onClick={() => openVehicleProfile(v, "documents")}>Documentos</button>
                          <button onClick={() => openVehicleProfile(v, "chronology")}>Histórico</button>
                          {v.creation_status === "active" && <button onClick={() => setReservationVehicle(v)}>{activeReservations[v.id] ? "Ver reserva" : "Reservar"}</button>}
                          <button onClick={() => setPublicationVehicle(v)}>{v.publication_status === "published" ? "Gerir publicação" : publicationBlocked(v) ? "Ver motivos" : "Publicar"}</button>
                          <button onClick={() => setDocumentVehicle(v)}>Gerar documento</button>
                        </div>
                      </details>
                    </div>
                    <div className="vehicle-circuit-card-meta"><span>{v.first_registration_year ?? "Ano —"}</span><span>{v.mileage_km.toLocaleString("pt-PT")} km</span><span>{money(v.sale_price_cents, v.currency_code)}</span></div>
                    {activeReservations[v.id] && <p className="vehicle-circuit-reservation">Reservada para {activeReservations[v.id].customer_name}</p>}
                    <div className="vehicle-circuit-card-status">
                      <span className={`stock-status status-${v.stock_status}`}>{v.creation_status === "draft" ? "Por concluir" : stockLabels[v.stock_status]}</span>
                      <span className={`stock-status publication-status ${v.publication_status === "published" ? "is-published" : "is-unpublished"}`}>{publicationLabels[v.publication_status]}</span>
                    </div>
                    <div className="vehicle-circuit-card-footer">
                      <select aria-label={`Mover ${v.make} ${v.model} para outra etapa`} value={v.operational_stage} disabled={!writable || stageSavingId === v.id} onChange={(event) => void moveVehicleStage(v, event.target.value)}>{Object.entries(stageLabels).map(([key, stageLabel]) => <option key={key} value={key}>{stageLabel}</option>)}</select>
                      <button type="button" className={publicationBlocked(v) ? "has-blockers" : ""} onClick={() => setPublicationVehicle(v)}>{v.publication_status === "published" ? "Publicação" : publicationBlocked(v) ? "Ver motivos" : "Publicar"}</button>
                    </div>
                  </article>
                ))}
                {stageVehicles.length > visibleStageVehicles.length && <button type="button" className="vehicle-circuit-more" onClick={() => setExpandedCircuitStage(stage)}>+ {stageVehicles.length - visibleStageVehicles.length} viaturas · Ver todas</button>}
              </div>
            </section>;
          })}
        </div>
        {expandedCircuitStage && <div className="vehicle-circuit-workspace-backdrop">
          <section className="vehicle-circuit-workspace" role="dialog" aria-modal="true" aria-label={`Organizar etapa ${stageLabels[expandedCircuitStage]}`}>
            <header>
              <div><p className="eyebrow">Organizar etapa</p><h2>{stageLabels[expandedCircuitStage]}</h2><span>{expandedCircuitVehicles.length} viaturas encontradas · {circuitSelectedIds.length} selecionadas</span></div>
              <button type="button" className="stock-close" onClick={() => setExpandedCircuitStage(null)} aria-label="Voltar ao circuito">×</button>
            </header>
            <div className="vehicle-circuit-workspace-toolbar">
              <input value={circuitQuery} onChange={(event) => setCircuitQuery(event.target.value)} placeholder="Pesquisar matrícula, marca ou modelo…" />
              <select value={circuitMake} onChange={(event) => { setCircuitMake(event.target.value); setCircuitModel("all"); }} aria-label="Filtrar por marca"><option value="all">Todas as marcas</option>{circuitMakes.map((make) => <option key={make} value={make}>{make}</option>)}</select>
              <select value={circuitModel} onChange={(event) => setCircuitModel(event.target.value)} aria-label="Filtrar por modelo"><option value="all">Todos os modelos</option>{circuitModels.map((model) => <option key={model} value={model}>{model}</option>)}</select>
              <select value={circuitYear} onChange={(event) => setCircuitYear(event.target.value)} aria-label="Filtrar por ano"><option value="all">Todos os anos</option>{circuitYears.map((year) => <option key={year} value={year}>{year}</option>)}</select>
              <select value={circuitSort} onChange={(event) => setCircuitSort(event.target.value)} aria-label="Ordenar viaturas"><option value="name">Nome A–Z</option><option value="year_desc">Ano mais recente</option><option value="year_asc">Ano mais antigo</option><option value="recent">Atualização recente</option></select>
            </div>
            <div className="vehicle-circuit-workspace-selection">
              <label><input type="checkbox" checked={expandedCircuitVehicles.length > 0 && expandedCircuitVehicles.every((vehicle) => circuitSelectedIds.includes(vehicle.id))} onChange={(event) => setCircuitSelectedIds((current) => event.target.checked ? [...new Set([...current, ...expandedCircuitVehicles.map((vehicle) => vehicle.id)])] : current.filter((id) => !expandedCircuitVehicles.some((vehicle) => vehicle.id === id)))} /> Selecionar resultados</label>
              <span>A seleção permanece ao voltar ao circuito.</span>
            </div>
            <div className="vehicle-circuit-workspace-grid">
              {expandedCircuitVehicles.map((v) => <article key={v.id} className={`vehicle-circuit-result ${circuitSelectedIds.includes(v.id) ? "is-selected" : ""}`}>
                <label className="vehicle-circuit-result-check"><input type="checkbox" checked={circuitSelectedIds.includes(v.id)} onChange={(event) => setCircuitSelectedIds((current) => event.target.checked ? [...new Set([...current, v.id])] : current.filter((id) => id !== v.id))} /><span>Selecionar</span></label>
                <button type="button" className="vehicle-circuit-identity" onClick={() => openVehicleProfile(v)}><span className="vehicle-circuit-photo">{vehicleThumbnails[v.id] ? <img src={vehicleThumbnails[v.id]} alt="" /> : <span>Sem foto</span>}</span><span><strong>{v.make} {v.model}</strong><small>{v.registration ?? v.stock_number ?? "Sem referência"}</small></span></button>
                <div className="vehicle-circuit-card-meta"><span>{v.first_registration_year ?? "Ano —"}</span><span>{v.mileage_km.toLocaleString("pt-PT")} km</span><span>{money(v.sale_price_cents, v.currency_code)}</span></div>
                <div className="vehicle-circuit-card-status"><span className={`stock-status status-${v.stock_status}`}>{v.creation_status === "draft" ? "Por concluir" : stockLabels[v.stock_status]}</span><span className={`stock-status publication-status ${v.publication_status === "published" ? "is-published" : "is-unpublished"}`}>{publicationLabels[v.publication_status]}</span></div>
              </article>)}
              {expandedCircuitVehicles.length === 0 && <div className="stock-empty"><strong>Nenhuma viatura corresponde aos filtros.</strong><span>Altere a pesquisa ou os filtros desta etapa.</span></div>}
            </div>
          </section>
        </div>}
        {circuitSelectedIds.length > 0 && <aside className="vehicle-circuit-selection-bar" aria-label="Viaturas selecionadas"><strong>{circuitSelectedIds.length} {circuitSelectedIds.length === 1 ? "viatura selecionada" : "viaturas selecionadas"}</strong><select value={circuitBatchTarget} onChange={(event) => setCircuitBatchTarget(event.target.value)} aria-label="Etapa de destino">{Object.entries(stageLabels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select><button type="button" onClick={() => void moveSelectedCircuitVehicles()} disabled={!writable || circuitBatchSaving}>{circuitBatchSaving ? "A mover…" : `Mover ${circuitSelectedIds.length}`}</button><button type="button" className="secondary" onClick={() => setCircuitSelectedIds([])}>Limpar seleção</button></aside>}
        </>
      ) : (
        <div className="vehicle-list">
          {filtered.map((v) => (
            <article key={v.id} className={`vehicle-row-shell ${activeReservations[v.id] ? "has-reservation" : ""}`}>
            <div className="vehicle-row" role="button" tabIndex={0} onClick={() => { setSelectedInitialTab("general"); setSelected(v); }} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setSelectedInitialTab("general"); setSelected(v); } }}>
              <span className="vehicle-thumbnail" aria-hidden="true">
                {vehicleThumbnails[v.id] ? (
                  <img src={vehicleThumbnails[v.id]} alt="" />
                ) : (
                  <span>Sem foto</span>
                )}
              </span>
              <span className="vehicle-ref">
                {v.registration}
                {useStockReference && v.stock_number
                  ? ` · ${v.stock_number}`
                  : ""}
              </span>
              <span className="vehicle-name">
                <strong>
                  {v.make} {v.model}
                </strong>
                <small>
                  {v.creation_status === "draft"
                    ? "Cadastro por concluir"
                    : `${acquisitionLabels[v.acquisition_type]} · ${stageLabels[v.operational_stage]}`}
                </small>
                <small className="vehicle-mobile-ref">{v.registration}</small>
              </span>
              <span>
                <strong>{v.first_registration_year ?? "—"}</strong>
                <small>{v.registration ?? "Sem matrícula"}</small>
              </span>
              <span>
                <strong>{v.mileage_km.toLocaleString("pt-PT")} km</strong>
                <small>{v.fuel_type ?? "Combustível não indicado"}</small>
              </span>
              <span className="vehicle-statuses">
                <span className={`stock-status status-${v.stock_status}`}>
                  {v.creation_status === "draft"
                    ? "Por concluir"
                    : stockLabels[v.stock_status]}
                </span>
                <span
                  className={`stock-status publication-status ${v.publication_status === "published" ? "is-published" : "is-unpublished"}`}
                  aria-label={`Publicação: ${publicationLabels[v.publication_status]}`}
                >
                  {publicationLabels[v.publication_status]}
                </span>
              </span>
              <span className="vehicle-publication-reasons">
                {publicationBlocked(v) && <button type="button" onClick={(event) => { event.stopPropagation(); setPublicationVehicle(v); }}>Ver motivos</button>}
              </span>
              <span className="vehicle-price">
                <strong>Venda: {money(v.sale_price_cents, v.currency_code)}</strong>
                <small>Compra: {money(v.purchase_price_cents, v.currency_code)}</small>
              </span>
            </div>
            {v.creation_status === "active" && (
              <details className="vehicle-row-menu">
                <summary aria-label={`Ações de ${v.make} ${v.model}`} title="Ações da viatura">⋮</summary>
                <div>
                  {!(["sold", "delivery", "delivered"] as string[]).includes(v.stock_status) && (
                    <button className={activeReservations[v.id] ? "reserved" : ""} onClick={() => setReservationVehicle(v)} title={activeReservations[v.id] ? `${activeReservations[v.id].customer_name} · até ${new Date(activeReservations[v.id].expires_at).toLocaleString("pt-PT")}${activeReservations[v.id].notes ? ` · ${activeReservations[v.id].notes}` : ""}` : "Criar reserva comercial"}>{activeReservations[v.id] ? "Ver reserva" : "Reservar"}</button>
                  )}
                  <button onClick={() => setPublicationVehicle(v)}>
                    {v.publication_status === "published" ? "Gerir publicação" : v.record_status === "complete" ? "PUBLICAR" : "Ver impedimentos"}
                  </button>
                  <button onClick={() => setDocumentVehicle(v)}>Gerar documento</button>
                </div>
              </details>
            )}
            </article>
          ))}
        </div>
      )}
      {(creating || selected) && (
        <VehicleDialog
          vehicle={selected}
          organizationId={organizationId}
          countryCode={countryCode}
          currencyCode={currencyCode}
          writable={writable}
          roleKey={roleKey}
          canManageLifecycle={canManageLifecycle}
          canManageFinance={canManageFinance}
          useStockReference={useStockReference}
          stockReferenceRequired={stockReferenceRequired}
          vehicleBrands={vehicleBrands}
          maxVehiclePhotos={maxVehiclePhotos}
          initialTab={selectedInitialTab}
          onClose={() => {
            setCreating(false);
            setSelected(null);
          }}
          onSaved={async (outcome) => {
            const edited = Boolean(selected);
            setCreating(false);
            setSelected(null);
            setMessage(
              outcome === "draft"
                ? "Cadastro guardado no stock como por concluir."
                : edited
                  ? "Viatura atualizada."
                  : "Viatura adicionada ao stock.",
            );
            await load();
          }}
        />
      )}
      {publicationVehicle && (
        <VehiclePublicationDialog
          vehicle={publicationVehicle}
          organizationId={organizationId}
          onClose={() => setPublicationVehicle(null)}
          onSaved={async (message) => {
            setPublicationVehicle(null);
            setMessage(message);
            await load();
          }}
          onEditVehicle={(tab) => {
            setPublicationVehicle(null);
            setSelectedInitialTab(tab);
            setSelected(publicationVehicle);
          }}
          onOpenSiteSettings={onOpenSiteSettings}
        />
      )}
      {documentVehicle && (
        <VehicleDocumentGenerator organizationId={organizationId} vehicleId={documentVehicle.id} onClose={() => setDocumentVehicle(null)} />
      )}
      {reservationVehicle && (
        <div className="stock-dialog-backdrop">
          <section className="reservation-dialog" role="dialog" aria-modal="true" aria-label={`Reserva de ${reservationVehicle.make} ${reservationVehicle.model}`}>
            <header><div><p className="eyebrow">Reserva comercial</p><h2>{reservationVehicle.make} {reservationVehicle.model}</h2><p>{reservationVehicle.registration ?? "Sem matrícula"}</p></div><button className="stock-close" onClick={() => setReservationVehicle(null)}>×</button></header>
            <ReservationPanel vehicle={reservationVehicle} organizationId={organizationId} currencyCode={currencyCode} writable={writable} roleKey={roleKey} canManageLifecycle={canManageLifecycle} onChanged={load} />
          </section>
        </div>
      )}
      {canManageLifecycle && deletions.length > 0 && (
        <details className="vehicle-deletion-history">
          <summary>Viaturas eliminadas — histórico preservado</summary>
          {deletions.map((item) => (
            <article key={item.id}>
              <div>
                <strong>{item.registration || item.stock_number}</strong>
                <span>
                  {item.make} {item.model}
                </span>
              </div>
              <p>{item.reason}</p>
              <time>{new Date(item.deleted_at).toLocaleString("pt-PT")}</time>
            </article>
          ))}
        </details>
      )}
    </section>
  );
}

function PendingVehiclePhotos({
  files,
  onChange,
  maxPhotos,
}: {
  files: File[];
  onChange: (files: File[]) => void;
  maxPhotos: number;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const dragPreviewRef = useRef<HTMLElement | null>(null);
  const draggedIndexRef = useRef<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const previews = useMemo(
    () => files.map((file) => ({ file, url: URL.createObjectURL(file) })),
    [files],
  );

  useEffect(
    () => () => previews.forEach((preview) => URL.revokeObjectURL(preview.url)),
    [previews],
  );

  const addFiles = (incoming: File[]) => {
    const photographs = incoming.filter((file) =>
      ["image/jpeg", "image/png", "image/webp"].includes(file.type),
    );
    const existing = new Set(
      files.map((file) => `${file.name}:${file.size}:${file.lastModified}`),
    );
    const unique = photographs.filter(
      (file) => !existing.has(`${file.name}:${file.size}:${file.lastModified}`),
    );
    const next = [...files, ...unique].slice(0, maxPhotos);
    onChange(next);
    if (!photographs.length) {
      setMessage("Escolha fotografias JPG, PNG ou WebP.");
    } else if (files.length + unique.length > maxPhotos) {
      setMessage(`Pode adicionar até ${maxPhotos} fotografias por viatura.`);
    } else {
      setMessage(null);
    }
  };

  const reorder = (sourceIndex: number, targetIndex: number) => {
    if (sourceIndex === null || sourceIndex === targetIndex) return;
    const next = [...files];
    const [moved] = next.splice(sourceIndex, 1);
    next.splice(targetIndex, 0, moved);
    onChange(next);
    draggedIndexRef.current = null;
  };

  const clearDragPreview = () => {
    dragPreviewRef.current?.remove();
    dragPreviewRef.current = null;
  };

  const startReorder = (event: DragEvent<HTMLElement>, index: number) => {
    draggedIndexRef.current = index;
    clearDragPreview();
    const preview = event.currentTarget.cloneNode(true) as HTMLElement;
    const bounds = event.currentTarget.getBoundingClientRect();
    preview.className = "media-drag-preview";
    preview.style.width = `${bounds.width}px`;
    preview.style.height = `${bounds.height}px`;
    document.body.appendChild(preview);
    dragPreviewRef.current = preview;
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(index));
    event.dataTransfer.setDragImage(
      preview,
      bounds.width / 2,
      bounds.height / 2,
    );
  };

  return (
    <fieldset className="stock-section vehicle-media-section pending-media-section">
      <legend>Fotografias</legend>
      <p className="stock-section-help">
        Adicione até {maxPhotos} fotografias. Arraste as miniaturas para definir a ordem;
        a primeira será a fotografia principal.
      </p>
      {message && (
        <button
          type="button"
          className="stock-notice"
          onClick={() => setMessage(null)}
        >
          {message} ×
        </button>
      )}
      <div className="media-usage">
        <strong>{files.length} de {maxPhotos} fotografias preparadas</strong>
        <span>{maxPhotos - files.length} lugares disponíveis</span>
      </div>
      <div className="pending-media-box">
        <div
          className="media-dropzone pending-file-dropzone"
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              inputRef.current?.click();
            }
          }}
          onDragEnter={(event) => event.preventDefault()}
          onDragOver={(event) => {
            event.preventDefault();
            event.dataTransfer.dropEffect = "copy";
          }}
          onDrop={(event) => {
            event.preventDefault();
            event.stopPropagation();
            addFiles(Array.from(event.dataTransfer.files));
          }}
        >
          <input
            ref={inputRef}
            hidden
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={(event) => {
              addFiles(Array.from(event.target.files ?? []));
              event.currentTarget.value = "";
            }}
          />
          <div className="pending-dropzone-heading">
            <strong>
              {previews.length
                ? "Adicionar mais fotografias"
                : "Arraste fotografias para aqui"}
            </strong>
            <span>ou clique para selecionar várias de uma vez</span>
          </div>
        </div>
        {previews.length > 0 && (
          <div className="media-grid pending-media-grid">
            {previews.map(({ file, url }, index) => (
              <article
                key={`${file.name}:${file.size}:${file.lastModified}`}
                draggable
                onClick={(event) => event.stopPropagation()}
                onDragStart={(event) => startReorder(event, index)}
                onDragEnd={() => {
                  draggedIndexRef.current = null;
                  clearDragPreview();
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  event.dataTransfer.dropEffect = "move";
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  const transferredIndex = Number(
                    event.dataTransfer.getData("text/plain"),
                  );
                  const sourceIndex = Number.isInteger(transferredIndex)
                    ? transferredIndex
                    : draggedIndexRef.current;
                  if (sourceIndex !== null) reorder(sourceIndex, index);
                }}
              >
                <img src={url} alt={file.name} />
                {index === 0 && <b>Principal</b>}
                <div>
                  <strong>{file.name}</strong>
                  <span>{Math.ceil(file.size / 1024)} KB</span>
                </div>
                <footer>
                  <span>Arraste para ordenar</span>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onChange(files.filter((_, item) => item !== index));
                    }}
                  >
                    Remover
                  </button>
                </footer>
              </article>
            ))}
          </div>
        )}
      </div>
    </fieldset>
  );
}

function PendingVehicleDocuments({
  documents,
  onChange,
}: {
  documents: PendingVehicleDocument[];
  onChange: (items: PendingVehicleDocument[]) => void;
}) {
  const [category, setCategory] = useState("Identificação"),
    [title, setTitle] = useState(""),
    [notes, setNotes] = useState(""),
    [file, setFile] = useState<File | null>(null),
    [message, setMessage] = useState<string | null>(null);
  const allowed = new Set([
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/webp",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ]);
  const add = () => {
    if (!file) return setMessage("Selecione o ficheiro.");
    if (!allowed.has(file.type))
      return setMessage("Utilize PDF, Word, JPG, PNG ou WebP.");
    if (file.size > 26214400)
      return setMessage("O documento não pode exceder 25 MB.");
    if (
      documents.some(
        (item) => item.file.name === file.name && item.file.size === file.size,
      )
    )
      return setMessage("Este ficheiro já foi preparado.");
    onChange([
      ...documents,
      {
        id: crypto.randomUUID(),
        category,
        title: title.trim() || file.name,
        notes: notes.trim(),
        file,
        preparedAt: new Date().toISOString(),
      },
    ]);
    setTitle("");
    setNotes("");
    setFile(null);
    setMessage(null);
  };
  return (
    <fieldset className="stock-section pending-document-section">
      <legend>Documentos</legend>
      <p className="stock-section-help">
        Prepare os documentos agora. Depois de guardar os dados principais,
        serão arquivados automaticamente na viatura.
      </p>
      {message && (
        <button
          type="button"
          className="stock-notice"
          onClick={() => setMessage(null)}
        >
          {message} ×
        </button>
      )}
      <div className="pending-document-editor">
        <div className="document-fields-row">
          <label>
            <span>Categoria</span>
            <select
              value={category}
              onChange={(event) => setCategory(event.target.value)}
            >
              <option>Identificação</option>
              <option>Aquisição</option>
              <option>Fatura</option>
              <option>Legalização</option>
              <option>Manutenção</option>
              <option>Garantia</option>
              <option>Outro</option>
            </select>
          </label>
          <label>
            <span className="field-label-row">
              <span>Nome do ficheiro</span>
              <em>Opcional</em>
            </span>
            <input
              type="text"
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </label>
        </div>
        <label className="document-notes-field">
          <span>Notas</span>
          <textarea
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            rows={3}
          />
        </label>
        <div className="document-actions-row">
          <label className="document-file-button">
            <input
              key={file?.name ?? "empty"}
              type="file"
              accept=".pdf,.docx,image/jpeg,image/png,image/webp"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            />
            <span>{file?.name ?? "Escolher ficheiro"}</span>
          </label>
          <button type="button" className="stock-secondary" onClick={add}>
            Adicionar documento
          </button>
        </div>
      </div>
      <div className="pending-document-list">
        {documents.map((item) => (
          <article key={item.id}>
            <div className="document-card-content">
              <strong>{item.title}</strong>
              <dl>
                <div>
                  <dt>Categoria</dt>
                  <dd>{item.category}</dd>
                </div>
                <div>
                  <dt>Tamanho</dt>
                  <dd>{Math.ceil(item.file.size / 1024)} KB</dd>
                </div>
                <div>
                  <dt>Data</dt>
                  <dd>
                    {new Intl.DateTimeFormat("pt-PT", {
                      dateStyle: "short",
                      timeStyle: "short",
                    }).format(new Date(item.preparedAt))}
                  </dd>
                </div>
                <div>
                  <dt>Ficheiro original</dt>
                  <dd>{item.file.name}</dd>
                </div>
                <div className="document-card-notes">
                  <dt>Notas</dt>
                  <dd>{item.notes || "Sem notas"}</dd>
                </div>
              </dl>
            </div>
            <button
              type="button"
              onClick={() =>
                onChange(
                  documents.filter((document) => document.id !== item.id),
                )
              }
            >
              Remover
            </button>
          </article>
        ))}
      </div>
    </fieldset>
  );
}

function VehicleDialog({
  vehicle,
  organizationId,
  countryCode,
  currencyCode,
  writable,
  roleKey,
  canManageLifecycle,
  canManageFinance,
  useStockReference,
  stockReferenceRequired,
  vehicleBrands,
  maxVehiclePhotos,
  initialTab,
  onClose,
  onSaved,
}: {
  vehicle: Vehicle | null;
  organizationId: string;
  countryCode: string;
  currencyCode: string;
  writable: boolean;
  roleKey: RoleKey;
  canManageLifecycle: boolean;
  canManageFinance: boolean;
  useStockReference: boolean;
  stockReferenceRequired: boolean;
  vehicleBrands: string[];
  maxVehiclePhotos: number;
  initialTab: VehicleProfileTab;
  onClose: () => void;
  onSaved: (outcome?: "saved" | "draft") => void;
}) {
  const [workingVehicle, setWorkingVehicle] = useState<Vehicle | null>(vehicle);
  const [formDirty, setFormDirty] = useState(false);
  const vehicleFormRef = useRef<HTMLFormElement | null>(null);
  const lookupTimerRef = useRef<number | null>(null);
  const lastLookedUpRegistration = useRef("");
  const [identificationMode, setIdentificationMode] = useState<"registration" | "manual">(
    !vehicle && countryCode === "PT" && currencyCode === "EUR" ? "registration" : "manual",
  );
  const [identifyingVehicle, setIdentifyingVehicle] = useState(false);
  const [purchasePriceInput, setPurchasePriceInput] = useState(
    formatCurrencyInput(vehicle?.purchase_price_cents),
  );
  const [salePriceInput, setSalePriceInput] = useState(
    formatCurrencyInput(vehicle?.sale_price_cents),
  );
  const [iucAmountInput, setIucAmountInput] = useState("");
  const [businessTerms, setBusinessTerms] = useState<VehicleBusinessTerms | null>(null);
  const [vatRatePreset, setVatRatePreset] = useState("23");
  const [vatRegime, setVatRegime] = useState("margin_scheme");
  const [customVatRate, setCustomVatRate] = useState("");
  const [pendingPhotos, setPendingPhotos] = useState<File[]>([]);
  const [pendingDocuments, setPendingDocuments] = useState<
    PendingVehicleDocument[]
  >([]);
  const currentVehicle = workingVehicle ?? vehicle;
  const createdHere = !vehicle && Boolean(workingVehicle);
  const [saving, setSaving] = useState(false),
    [error, setError] = useState<string | null>(null),
    [notice, setNotice] = useState<string | null>(null),
    [history, setHistory] = useState<VehicleEvent[]>([]),
    [chosenStatus, setChosenStatus] = useState(
      vehicle?.stock_status ?? "in_stock",
    );
  const [acquisitionType, setAcquisitionType] = useState(
    vehicle?.acquisition_type === "consignment"
      ? "owned"
      : vehicle?.acquisition_type ?? "owned",
  );
  const [fuelType, setFuelType] = useState(vehicle?.fuel_type ?? "");
  const [recordStatus, setRecordStatus] = useState<"incomplete" | "complete">(
    vehicle?.record_status ?? "incomplete",
  );
  const [owners, setOwners] = useState<ConsignmentOwner[]>([]);
  const [stockBranches, setStockBranches] = useState<StockBranch[]>([]);
  const [expectedWarrantyProducts, setExpectedWarrantyProducts] = useState<ExpectedWarrantyProduct[]>([]);
  const [expectedWarrantyProviders, setExpectedWarrantyProviders] = useState<ExpectedWarrantyProvider[]>([]);
  const [expectedWarrantySettings, setExpectedWarrantySettings] = useState<ExpectedWarrantySettings>(null);
  const [ownerId, setOwnerId] = useState("");
  const [newOwnerKind, setNewOwnerKind] = useState<CustomerKind>("individual");
  const [consignmentDialogOpen, setConsignmentDialogOpen] = useState(false);
  const [consignmentConfigured, setConsignmentConfigured] = useState(false);
  const [lifecycleChoice, setLifecycleChoice] = useState("");
  const [agreement, setAgreement] = useState<ConsignmentAgreement | null>(null);
  const [commissionType, setCommissionType] = useState(
    vehicle?.acquisition_type === "consignment" ? "margin" : "margin",
  );
  const [specification, setSpecification] =
    useState<VehicleSpecification | null>(null);
  const [equipment, setEquipment] = useState<EquipmentItem[]>([]);
  const [selectedEquipment, setSelectedEquipment] = useState<string[]>([]);
  const [equipmentSearch, setEquipmentSearch] = useState("");
  const [customEquipmentName, setCustomEquipmentName] = useState("");
  const [customEquipmentCategory, setCustomEquipmentCategory] =
    useState("comfort");
  const [profileTab, setProfileTab] = useState<
    | "general"
    | "technical"
    | "equipment"
    | "media"
    | "documents"
    | "marketing"
    | "commercial"
    | "consignment"
    | "chronology"
  >(initialTab);
  const [consignmentReady, setConsignmentReady] = useState(!vehicle);
  const reservationBlocksLifecycle =
    currentVehicle?.stock_status === "reserved";
  const deletionBlockedByKnownProcess = Boolean(
    reservationBlocksLifecycle ||
      (currentVehicle && saleStatuses.has(currentVehicle.stock_status)) ||
      agreement ||
      currentVehicle?.acquisition_type === "trade_in",
  );
  useEffect(() => {
    void Promise.all([
      supabase
        .from("organization_branches")
        .select("id,name,code,is_headquarters")
        .eq("organization_id", organizationId)
        .eq("active", true)
        .order("is_headquarters", { ascending: false })
        .order("name"),
      supabase.rpc("after_sales_panel", { p_organization_id: organizationId }),
    ]).then(([branchResult, warrantyResult]) => {
      if (branchResult.error) setError(`Não foi possível carregar as filiais: ${branchResult.error.message}`);
      else setStockBranches((branchResult.data ?? []) as StockBranch[]);
      if (warrantyResult.error) setError(`Não foi possível carregar as garantias: ${warrantyResult.error.message}`);
      else {
        setExpectedWarrantySettings((warrantyResult.data?.settings ?? null) as ExpectedWarrantySettings);
        setExpectedWarrantyProviders((warrantyResult.data?.providers ?? []) as ExpectedWarrantyProvider[]);
        setExpectedWarrantyProducts((warrantyResult.data?.products ?? []) as ExpectedWarrantyProduct[]);
      }
    });
  }, [organizationId]);
  useEffect(() => {
    void Promise.all([
      supabase
        .from("stand_customers")
        .select("id,display_name,customer_type,customer_role,legal_name,trade_name,tax_identifier,email,phone,address,notes")
        .eq("organization_id", organizationId)
        .eq("status", "active")
        .in("customer_role", ["owner", "both"])
        .order("display_name"),
      currentVehicle
        ? supabase
            .from("vehicle_consignment_agreements")
            .select(
              "id,owner_id,status,owner_amount_cents,commission_type,commission_value,minimum_sale_price_cents,starts_on,ends_on,terms,internal_notes",
            )
            .eq("vehicle_id", currentVehicle.id)
            .maybeSingle()
        : Promise.resolve({ data: null, error: null }),
      supabase
        .from("consignment_owners")
        .select("id,customer_id")
        .eq("organization_id", organizationId),
    ]).then(([ownerResult, agreementResult, linksResult]) => {
      if (ownerResult.error) setError(ownerResult.error.message);
      setOwners((ownerResult.data ?? []) as ConsignmentOwner[]);
      if (agreementResult.error) {
        setError(`Não foi possível carregar o acordo de consignação: ${agreementResult.error.message}`);
      }
      if (linksResult.error) {
        setError(`Não foi possível carregar o proprietário da consignação: ${linksResult.error.message}`);
      }
      const current = agreementResult.data as ConsignmentAgreement | null;
      setAgreement(current);
      setCommissionType(current?.commission_type ?? "margin");
      if (current) {
        setConsignmentConfigured(true);
        setAcquisitionType("consignment");
        const link = (linksResult.data ?? []).find((item) => item.id === current.owner_id);
        setOwnerId(link?.customer_id ?? "");
      } else {
        setConsignmentConfigured(false);
        if (currentVehicle?.acquisition_type === "consignment") setAcquisitionType("owned");
      }
      setConsignmentReady(true);
    });
  }, [organizationId, currentVehicle?.id]);
  useEffect(() => {
    if (!currentVehicle) return;
    void supabase
      .from("vehicle_events")
      .select("id,event_type,before_data,after_data,created_at")
      .eq("vehicle_id", currentVehicle.id)
      .order("created_at", { ascending: false })
      .limit(30)
      .then(({ data, error }) => {
        if (error) setError(error.message);
        else setHistory((data ?? []) as VehicleEvent[]);
      });
  }, [currentVehicle?.id]);
  useEffect(() => {
    if (!currentVehicle) return;
    void supabase.from("vehicle_business_terms").select("business_type,vat_regime,vat_rate_percent,vat_itemized,vat_deductible,available_for_sale_on,fixed_price,accepts_exchange,accepts_trade_in,price_excludes_isv,financing_available,iuc_amount_cents,estimated_vat_cents").eq("vehicle_id",currentVehicle.id).maybeSingle().then(({data,error}) => {
      if (error) setError(`Não foi possível carregar os dados fiscais: ${error.message}`);
      const terms=data as VehicleBusinessTerms|null;
      setBusinessTerms(terms);
      if (terms) {
        setVatRegime(terms.vat_regime);
        setIucAmountInput(formatCurrencyInput(terms.iuc_amount_cents));
        const rate=String(Number(terms.vat_rate_percent));
        if (["23","22","16","13","12","9","6","5","0"].includes(rate)) setVatRatePreset(rate);
        else { setVatRatePreset("custom"); setCustomVatRate(rate); }
      }
    });
  }, [currentVehicle?.id]);
  useEffect(() => {
    void Promise.all([
      supabase
        .from("vehicle_equipment_catalog")
        .select("id,organization_id,category,name,sort_order")
        .eq("country_code", "PT")
        .eq("active", true)
        .or(`organization_id.is.null,organization_id.eq.${organizationId}`)
        .order("category")
        .order("sort_order")
        .order("name"),
      currentVehicle
        ? supabase
            .from("vehicle_specifications")
            .select("*")
            .eq("vehicle_id", currentVehicle.id)
            .maybeSingle()
        : Promise.resolve({ data: null, error: null }),
      currentVehicle
        ? supabase
            .from("vehicle_equipment")
            .select("equipment_id")
            .eq("vehicle_id", currentVehicle.id)
        : Promise.resolve({ data: [], error: null }),
    ]).then(([catalogResult, specResult, selectedResult]) => {
      if (catalogResult.error || specResult.error || selectedResult.error)
        setError(
          catalogResult.error?.message ??
            specResult.error?.message ??
            selectedResult.error?.message ??
            null,
        );
      setEquipment((catalogResult.data ?? []) as EquipmentItem[]);
      setSpecification(specResult.data as VehicleSpecification | null);
      setSelectedEquipment(
        ((selectedResult.data ?? []) as { equipment_id: string }[]).map(
          (item) => item.equipment_id,
        ),
      );
    });
  }, [organizationId, currentVehicle?.id]);
  const addCustomEquipment = async () => {
    const name = customEquipmentName.trim();
    if (!name || !writable) return;
    const { data, error } = await supabase
      .from("vehicle_equipment_catalog")
      .insert({
        organization_id: organizationId,
        country_code: "PT",
        category: customEquipmentCategory,
        name,
        created_by: (await supabase.auth.getUser()).data.user?.id,
      })
      .select("id,organization_id,category,name,sort_order")
      .single();
    if (error) {
      setError(error.message);
      return;
    }
    const item = data as EquipmentItem;
    setEquipment((current) => [...current, item]);
    setSelectedEquipment((current) => [...current, item.id]);
    setCustomEquipmentName("");
  };
  const lifecycleAction = async (
    action: "pause" | "archive" | "reactivate",
  ) => {
    if (!currentVehicle) return;
    const labels = {
      pause: "pausa",
      archive: "arquivo",
      reactivate: "reativação",
    };
    const reason = window.prompt(`Indique o motivo da ${labels[action]}:`);
    if (!reason?.trim()) return;
    setSaving(true);
    setError(null);
    const { error } = await supabase.rpc("change_vehicle_lifecycle", {
      p_vehicle_id: currentVehicle.id,
      p_action: action,
      p_reason: reason.trim(),
    });
    setSaving(false);
    if (error) return setError(error.message);
    onSaved("saved");
  };
  const reverseSale = async () => {
    if (!currentVehicle) return;
    const reason = window.prompt(
      "Explique por que esta venda está a ser revertida:",
    );
    if (!reason?.trim()) return;
    setSaving(true);
    setError(null);
    const { error } = await supabase.rpc("reverse_vehicle_sale", {
      p_vehicle_id: currentVehicle.id,
      p_reason: reason.trim(),
      p_next_status: "in_stock",
    });
    setSaving(false);
    if (error) return setError(error.message);
    onSaved("saved");
  };
  const applyLifecycleChoice = async () => {
    if (lifecycleChoice === "reverse") await reverseSale();
    else if (["pause", "archive", "reactivate"].includes(lifecycleChoice))
      await lifecycleAction(
        lifecycleChoice as "pause" | "archive" | "reactivate",
      );
  };
  const deleteVehicle = async () => {
    if (
      !currentVehicle ||
      !window.confirm(
        "Eliminar esta viatura do stock? O sistema só permitirá a operação se não existir qualquer processo associado.",
      )
    )
      return;
    const reason = window.prompt(
      "Indique o motivo da eliminação para o histórico de auditoria:",
    );
    if (!reason?.trim()) return;
    setSaving(true);
    setError(null);
    const { error } = await supabase.rpc("delete_clean_vehicle", {
      p_vehicle_id: currentVehicle.id,
      p_reason: reason.trim(),
    });
    setSaving(false);
    if (error) return setError(error.message);
    onSaved();
  };
  const fillVehicleField = (name: string, value: string | number | null) => {
    if (value === null || value === undefined || value === "") return;
    const field = vehicleFormRef.current?.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement | null;
    if (!field) return;
    field.value = String(value);
    field.dispatchEvent(new Event("input", { bubbles: true }));
    field.dispatchEvent(new Event("change", { bubbles: true }));
  };
  const identifyVehicle = async (registration: string) => {
    if (identificationMode !== "registration" || identifyingVehicle) return;
    const formatted = formatPortugueseRegistration(registration);
    if (!/^[A-Z0-9]{2}(?:-[A-Z0-9]{2}){2}$/.test(formatted) || lastLookedUpRegistration.current === formatted) return;
    lastLookedUpRegistration.current = formatted;
    setIdentifyingVehicle(true);
    setError(null);
    const { data, error: lookupError } = await supabase.functions.invoke("identify-portuguese-vehicle", {
      body: { registration: formatted, organization_id: organizationId },
    });
    setIdentifyingVehicle(false);
    if (lookupError || data?.error || !data?.vehicle) {
      lastLookedUpRegistration.current = "";
      setError(data?.error ?? lookupError?.message ?? "Não foi possível preencher a viatura pela matrícula. Pode tentar novamente ou escolher o preenchimento manual.");
      return;
    }
    const identified = data.vehicle as Record<string, string | number | null>;
    for (const [name, value] of Object.entries({
      registration: identified.registration,
      make: identified.make,
      model: identified.model,
      version: identified.version,
      vin: identified.vin,
      year: identified.first_registration_year,
      registration_date: identified.registration_date,
      engine_capacity_cc: identified.engine_capacity_cc,
      color: identified.color,
      seats: identified.seats,
      doors: identified.doors,
      transmission: identified.transmission,
      body_type: identified.body_type,
    })) fillVehicleField(name, value);
    if (identified.fuel_type) {
      setFuelType(String(identified.fuel_type));
      fillVehicleField("fuel_type", identified.fuel_type);
    }
    setFormDirty(true);
    setNotice("Dados da viatura preenchidos pela matrícula. Confirme apenas a informação comercial antes de guardar.");
  };
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!writable) return;
    setError(null);
    if (acquisitionType === "consignment" && !consignmentConfigured && !agreement) {
      setProfileTab("general");
      setConsignmentDialogOpen(true);
      setError("Preencha e guarde a consignação antes de guardar a viatura.");
      return;
    }
    const f = new FormData(event.currentTarget);
    const portugueseRegistration = countryCode === "PT" && currencyCode === "EUR";
    const registration = portugueseRegistration
      ? formatPortugueseRegistration(String(f.get("registration") || ""))
      : String(f.get("registration") || "").trim().toUpperCase();
    const requiredFields = [
      { name: "registration", label: "Matrícula" },
      { name: "make", label: "Marca" },
      { name: "model", label: "Modelo" },
      ...(useStockReference && stockReferenceRequired
        ? [{ name: "stock_number", label: "Referência de stock" }]
        : []),
    ];
    const missing = requiredFields.filter(
      (field) => !String(f.get(field.name) ?? "").trim(),
    );
    if (missing.length) {
      setProfileTab("general");
      setError(
        `Falta preencher: ${missing.map((field) => field.label).join(", ")}.`,
      );
      window.requestAnimationFrame(() => {
        const first = event.currentTarget.querySelector<HTMLElement>(
          `[name="${missing[0].name}"]`,
        );
        first?.scrollIntoView({ behavior: "smooth", block: "center" });
        first?.focus();
      });
      return;
    }
    if (portugueseRegistration && !/^[A-Z0-9]{2}(?:-[A-Z0-9]{2}){2}$/.test(registration)) {
      setProfileTab("general");
      setError("A matrícula portuguesa deve ter o formato XX-XX-XX.");
      window.requestAnimationFrame(() => {
        const field = event.currentTarget.querySelector<HTMLInputElement>('[name="registration"]');
        field?.scrollIntoView({ behavior: "smooth", block: "center" });
        field?.focus();
      });
      return;
    }
    const automaticRecordStatus: "incomplete" | "complete" = missing.length
      ? "incomplete"
      : "complete";
    const requestedPublication = currentVehicle?.publication_status ?? "draft";
    setSaving(true);
    const payload = {
      organization_id: organizationId,
      stock_number: useStockReference
        ? String(f.get("stock_number") ?? "").trim() || null
        : null,
      make: String(f.get("make") ?? "").trim(),
      model: String(f.get("model") ?? "").trim(),
      version: String(f.get("version") || "") || null,
      registration: registration || null,
      vin:
        String(f.get("vin") || "")
          .toUpperCase()
          .replace(/[^A-Z0-9]/g, "") || null,
      first_registration_year: f.get("year") ? Number(f.get("year")) : null,
      first_registration_country_code: String(f.get("first_registration_country_code")||"")||null,
      registration_date: String(f.get("registration_date") || "") || null,
      fuel_type: String(f.get("fuel_type") || "") || null,
      transmission: String(f.get("transmission") || "") || null,
      mileage_km: Number(f.get("mileage") || 0),
      color: (() => {
        const color = String(f.get("color") || "");
        const finish = paintFinishLabels[String(f.get("paint_finish") || "")];
        return color ? `${color}${finish ? ` · ${finish}` : ""}` : null;
      })(),
      body_type: String(f.get("body_type") || "") || null,
      engine_capacity_cc: f.get("engine_capacity_cc")
        ? Number(f.get("engine_capacity_cc"))
        : null,
      power_kw: f.get("power_kw") ? Number(f.get("power_kw")) : null,
      doors: f.get("doors") ? Number(f.get("doors")) : null,
      seats: f.get("seats") ? Number(f.get("seats")) : null,
      purchase_date: String(f.get("purchase_date") || "") || null,
      supplier_name: String(f.get("supplier_name") || "") || null,
      branch_id: String(f.get("branch_id") || "") || null,
      stock_location: stockBranches.find((branch) => branch.id === String(f.get("branch_id") || ""))?.name ?? null,
      expected_warranty_type: String(f.get("expected_warranty") || "").startsWith("own:") ? "own" : String(f.get("expected_warranty") || "").startsWith("product:") ? "acquired" : null,
      expected_warranty_product_id: String(f.get("expected_warranty") || "").startsWith("product:") ? String(f.get("expected_warranty")).slice(8) : null,
      warranty_months: String(f.get("expected_warranty") || "").startsWith("own:")
        ? Number(String(f.get("expected_warranty")).slice(4))
        : expectedWarrantyProducts.find((product) => product.id === String(f.get("expected_warranty") || "").slice(8))?.duration_months ?? null,
      internal_notes: String(f.get("internal_notes") || "") || null,
      record_status: automaticRecordStatus,
      purchase_price_cents: parseCurrencyInputToCents(purchasePriceInput),
      sale_price_cents: parseCurrencyInputToCents(salePriceInput),
      currency_code: currencyCode,
      acquisition_type: acquisitionType,
      operational_stage: String(f.get("operational_stage")),
      stock_status: chosenStatus,
      pause_reason: String(f.get("pause_reason") || "") || null,
      publication_status: requestedPublication,
      featured: f.get("featured") === "on",
      description: String(f.get("description") || "") || null,
    };
    const {
      data: { user },
    } = await supabase.auth.getUser();
    let targetVehicle = currentVehicle;
    if (!targetVehicle) {
      const draftResult = await supabase.rpc("create_vehicle_draft", {
        p_organization_id: organizationId,
        p_stock_number: payload.stock_number,
        p_registration: payload.registration,
        p_make: payload.make,
        p_model: payload.model,
        p_acquisition_type: acquisitionType,
      });
      if (draftResult.error) {
        if (draftResult.error.code === "23505") {
          const existing = await supabase
            .from("vehicles")
            .select("*")
            .eq("organization_id", organizationId)
            .eq("registration", payload.registration)
            .neq("creation_status", "discarded")
            .maybeSingle();
          if (existing.data?.creation_status === "draft") {
            targetVehicle = existing.data as Vehicle;
            setNotice("O cadastro já existente foi retomado.");
          } else {
            setSaving(false);
            setError("Já existe uma viatura com esta matrícula neste stand.");
            return;
          }
        } else {
          setSaving(false);
          setError(draftResult.error.message);
          return;
        }
      } else {
        targetVehicle = draftResult.data as Vehicle;
      }
    }
    const vehicleResult = await supabase
      .from("vehicles")
      .update({
        ...payload,
        publication_status:
          targetVehicle.creation_status === "draft"
            ? "draft"
            : payload.publication_status,
      })
      .eq("id", targetVehicle.id)
      .eq("organization_id", organizationId)
      .select("*")
      .single();
    if (vehicleResult.error) {
      setSaving(false);
      setError(vehicleResult.error.message);
      return;
    }
    if (
      vehicleResult.data.purchase_price_cents !== payload.purchase_price_cents ||
      vehicleResult.data.sale_price_cents !== payload.sale_price_cents
    ) {
      setSaving(false);
      setError(
        "Os preços devolvidos pelo sistema não coincidem com os valores introduzidos. A ficha não será apresentada como guardada.",
      );
      return;
    }
    const savedVehicle = vehicleResult.data as Vehicle;
    const vehicleId = savedVehicle.id;
    if (canManageFinance) {
      const selectedVatRate = vatRatePreset === "custom" ? Number(customVatRate.replace(",",".")) : Number(vatRatePreset);
      if (!Number.isFinite(selectedVatRate) || selectedVatRate < 0 || selectedVatRate > 100) {
        setSaving(false);
        setError("Indique uma taxa de IVA válida entre 0% e 100%.");
        return;
      }
      const termsResult = await supabase.rpc("save_vehicle_business_terms", {
        p_vehicle_id: vehicleId,
        p_business_type: String(f.get("business_type") || "used"),
        p_vat_regime: String(f.get("vat_regime") || "margin_scheme"),
        p_vat_rate_percent: selectedVatRate,
        p_vat_itemized: f.get("vat_itemized") === "on",
        p_vat_deductible: f.get("vat_deductible") === "on",
        p_available_for_sale_on: String(f.get("available_for_sale_on") || "") || null,
        p_fixed_price: f.get("fixed_price") === "on",
        p_accepts_exchange: f.get("accepts_exchange") === "on",
        p_accepts_trade_in: f.get("accepts_trade_in") === "on",
        p_price_excludes_isv: f.get("price_excludes_isv") === "on",
        p_financing_available: f.get("financing_available") === "on",
        p_iuc_amount_cents: parseCurrencyInputToCents(iucAmountInput) ?? 0,
      });
      if (termsResult.error) {
        setSaving(false);
        setError(`A viatura foi guardada, mas a integração financeira não: ${termsResult.error.message}`);
        return;
      }
      setBusinessTerms(termsResult.data as VehicleBusinessTerms);
    }
    const profileResult = await supabase.rpc("save_vehicle_profile", {
      p_vehicle_id: vehicleId,
      p_specifications: {
        gears: String(f.get("gears") || ""),
        traction: String(f.get("traction") || ""),
        co2_g_km: String(f.get("co2_g_km") || ""),
        previous_owners: String(f.get("previous_owners") || ""),
        inspection_until: String(f.get("inspection_until") || ""),
        factory_warranty_until: String(f.get("factory_warranty_until") || ""),
        factory_warranty_km: String(f.get("factory_warranty_km") || ""),
        full_service_history: f.get("full_service_history") === "on",
        second_key: f.get("second_key") === "on",
        non_smoker: f.get("non_smoker") === "on",
        classic_vehicle: f.get("classic_vehicle") === "on",
        last_service_on: String(f.get("last_service_on") || ""),
        last_service_km: String(f.get("last_service_km") || ""),
        service_interval_months: String(f.get("service_interval_months") || ""),
        service_interval_km: String(f.get("service_interval_km") || ""),
        timing_belt_on: String(f.get("timing_belt_on") || ""),
        timing_belt_km: String(f.get("timing_belt_km") || ""),
        battery_capacity_kwh: String(f.get("battery_capacity_kwh") || ""),
        battery_health_percent: String(f.get("battery_health_percent") || ""),
        electric_range_km: String(f.get("electric_range_km") || ""),
        consumption_kwh_100km: String(f.get("consumption_kwh_100km") || ""),
        charging_connector: String(f.get("charging_connector") || ""),
        max_charge_kw: String(f.get("max_charge_kw") || ""),
      },
      p_equipment_ids: selectedEquipment,
    });
    if (profileResult.error) {
      setSaving(false);
      setError(
        `Os dados principais foram guardados, mas a ficha técnica não: ${profileResult.error.message}`,
      );
      return;
    }
    const agreementFormSubmitted =
      f.has("agreement_status") && f.has("starts_on");
    if (
      canManageLifecycle &&
      acquisitionType === "consignment" &&
      !agreement &&
      !agreementFormSubmitted
    ) {
      setSaving(false);
      setError(
        "Preencha e guarde o acordo de consignação antes de concluir o cadastro da viatura.",
      );
      setProfileTab("general");
      setConsignmentDialogOpen(true);
      return;
    }
    if (
      canManageLifecycle &&
      acquisitionType === "consignment" &&
      agreementFormSubmitted
    ) {
      let selectedCustomerId = ownerId;
      let selectedCustomer = owners.find((item) => item.id === selectedCustomerId);
      if (selectedCustomerId === "__new__") {
        const requestedRole = String(f.get("customer_role"));
        if (!['owner', 'both'].includes(requestedRole)) {
          setSaving(false);
          setError("Uma viatura consignada só pode ser vinculada a um cliente Proprietário ou Comprador e proprietário.");
          setProfileTab("general");
          return;
        }
        const identityErrors = validateCustomerIdentity({
          kind: newOwnerKind,
          country: currencyCode === "BRL" ? "BR" : "PT",
          displayName: String(f.get("owner_name") || ""),
          legalName: String(f.get("owner_legal_name") || ""),
          tradeName: String(f.get("owner_trade_name") || ""),
          taxIdentifier: String(f.get("owner_tax_id") || ""),
          email: String(f.get("owner_email") || ""),
        });
        if (identityErrors.length) {
          setSaving(false);
          setError(identityErrors[0]);
          return;
        }
        const customerResult = await supabase
          .from("stand_customers")
          .insert({
            organization_id: organizationId,
            customer_type: newOwnerKind,
            customer_role: requestedRole,
            display_name: String(f.get("owner_name") ?? "").trim(),
            legal_name: newOwnerKind === "company" ? String(f.get("owner_legal_name") || "").trim() : null,
            trade_name: newOwnerKind === "company" ? String(f.get("owner_trade_name") || "").trim() : null,
            tax_identifier: String(f.get("owner_tax_id") || "").replace(/\D/g, "") || null,
            email: String(f.get("owner_email") || "") ? normalizeEmail(String(f.get("owner_email"))) : null,
            phone: String(f.get("owner_phone") || "") || null,
            address: String(f.get("owner_address") || "") || null,
            notes: String(f.get("owner_notes") || "") || null,
            relationship_status: "prospect",
            created_by: user?.id,
          })
          .select("id,display_name,customer_type,customer_role,legal_name,trade_name,tax_identifier,email,phone,address,notes")
          .single();
        if (customerResult.error) {
          setSaving(false);
          setError(
            `A viatura foi guardada, mas o cliente proprietário não: ${customerResult.error.message}`,
          );
          return;
        }
        selectedCustomer = customerResult.data as ConsignmentOwner;
        selectedCustomerId = selectedCustomer.id;
        setOwners((current) => [...current, selectedCustomer as ConsignmentOwner]);
      }
      if (!selectedCustomerId || !selectedCustomer) {
        setSaving(false);
        setError("Escolha o cliente proprietário da viatura consignada.");
        setProfileTab("general");
        return;
      }
      const existingOwner = await supabase
        .from("consignment_owners")
        .select("id")
        .eq("customer_id", selectedCustomerId)
        .maybeSingle();
      if (existingOwner.error) {
        setSaving(false);
        setError(existingOwner.error.message);
        return;
      }
      let selectedOwnerId = existingOwner.data?.id;
      if (!selectedOwnerId) {
        const ownerResult = await supabase.from("consignment_owners").insert({
          organization_id: organizationId,
          customer_id: selectedCustomerId,
          owner_type: selectedCustomer.customer_type,
          display_name: selectedCustomer.display_name,
          tax_id: selectedCustomer.tax_identifier,
          email: selectedCustomer.email,
          phone: selectedCustomer.phone,
          address: selectedCustomer.address,
          notes: selectedCustomer.notes,
          created_by: user?.id,
        }).select("id").single();
        if (ownerResult.error) {
          setSaving(false);
          setError(`A viatura foi guardada, mas o vínculo do proprietário não: ${ownerResult.error.message}`);
          return;
        }
        selectedOwnerId = ownerResult.data.id;
      }
      const startsOn = String(f.get("starts_on") ?? "").trim();
      const rawEndsOn = String(f.get("ends_on") ?? "").trim();
      const endsOn =
        rawEndsOn && rawEndsOn !== "null" && rawEndsOn !== "undefined"
          ? rawEndsOn
          : null;
      if (!startsOn || startsOn === "null" || startsOn === "undefined") {
        setSaving(false);
        setError("Indique a data de início do acordo de consignação.");
        setConsignmentDialogOpen(true);
        return;
      }
      const agreementPayload = {
        organization_id: organizationId,
        vehicle_id: vehicleId,
        owner_id: selectedOwnerId,
        status: String(f.get("agreement_status")),
        owner_amount_cents: Math.round(
          Number(f.get("owner_amount") || 0) * 100,
        ),
        commission_type: String(f.get("commission_type")),
        commission_value: f.get("commission_value")
          ? Number(f.get("commission_value"))
          : null,
        minimum_sale_price_cents: f.get("minimum_sale_price")
          ? Math.round(Number(f.get("minimum_sale_price")) * 100)
          : null,
        currency_code: currencyCode,
        starts_on: startsOn,
        ends_on: endsOn,
        terms: String(f.get("terms") || "") || null,
        internal_notes: String(f.get("consignment_notes") || "") || null,
        created_by: user?.id,
      };
      const agreementResult = agreement
        ? await supabase
            .from("vehicle_consignment_agreements")
            .update(agreementPayload)
            .eq("id", agreement.id)
            .select("id,owner_id,status,owner_amount_cents,commission_type,commission_value,minimum_sale_price_cents,starts_on,ends_on,terms,internal_notes")
            .single()
        : await supabase
            .from("vehicle_consignment_agreements")
            .insert(agreementPayload)
            .select("id,owner_id,status,owner_amount_cents,commission_type,commission_value,minimum_sale_price_cents,starts_on,ends_on,terms,internal_notes")
            .single();
      if (agreementResult.error) {
        setSaving(false);
        setError(
          `A viatura foi guardada, mas o acordo não: ${agreementResult.error.message}`,
        );
        return;
      }
      setAgreement(agreementResult.data as ConsignmentAgreement);
      setOwnerId(selectedCustomerId);
      setConsignmentConfigured(true);
      setConsignmentDialogOpen(false);
      setProfileTab("general");
      setNotice("Consignação guardada. Pode consultar ou editar o acordo na aba Consignado.");
    } else if (
      canManageLifecycle &&
      agreement &&
      acquisitionType !== "consignment"
    ) {
      const endResult = await supabase
        .from("vehicle_consignment_agreements")
        .update({
          status: "ended",
          ends_on: new Date().toISOString().slice(0, 10),
        })
        .eq("id", agreement.id);
      if (endResult.error) {
        setSaving(false);
        setError(endResult.error.message);
        return;
      }
    }
    if (!currentVehicle) {
      setWorkingVehicle(savedVehicle);
      setFormDirty(false);
      setNotice(
        acquisitionType === "consignment"
          ? "Cadastro e vínculo com o cliente proprietário guardados. Pode continuar com fotografias e documentos."
          : pendingPhotos.length || pendingDocuments.length
            ? "Cadastro guardado no stock como por concluir. As fotografias e os documentos selecionados estão a ser carregados."
            : "Cadastro guardado no stock como por concluir.",
      );
      setSaving(false);
      setError(null);
      return;
    }
    if (savedVehicle.creation_status === "draft" && automaticRecordStatus === "complete") {
      const confirmation = await supabase.rpc("confirm_vehicle_draft", {
        p_vehicle_id: savedVehicle.id,
      });
      if (confirmation.error) {
        setSaving(false);
        setError(confirmation.error.message);
        return;
      }
      if (requestedPublication !== "draft") {
        const publicationResult = await supabase
          .from("vehicles")
          .update({ publication_status: requestedPublication })
          .eq("id", savedVehicle.id)
          .eq("organization_id", organizationId);
        if (publicationResult.error) {
          setSaving(false);
          setError(publicationResult.error.message);
          return;
        }
      }
    }
    setSaving(false);
    setFormDirty(false);
    onSaved(
      savedVehicle.creation_status === "draft" && automaticRecordStatus === "incomplete"
        ? "draft"
        : "saved",
    );
  };
  const closeDialog = async () => {
    if (
      formDirty &&
      !window.confirm(
        currentVehicle
          ? "Existem alterações recentes por guardar. Sair mantém a última versão guardada no stock e descarta apenas estas alterações. Deseja sair?"
          : "Este cadastro ainda não foi guardado. Deseja sair e descartar os dados preenchidos?",
      )
    )
      return;
    if (createdHere) onSaved("draft");
    else onClose();
  };
  return (
    <div className="stock-dialog-backdrop">
      <section className="stock-dialog vehicle-profile-dialog" role="dialog" aria-modal="true">
        <header>
          <div>
            <p className="eyebrow">
              {currentVehicle ? "Ficha da viatura" : "Nova entrada"}
            </p>
            <h2>
              {currentVehicle
                ? `${currentVehicle.make} ${currentVehicle.model}`
                : "Adicionar viatura"}
            </h2>
            <p>
              Registe a origem, acompanhe a etapa física e controle o estado
              comercial sem perder histórico.
            </p>
          </div>
          <button className="stock-close" onClick={() => void closeDialog()}>
            ×
          </button>
        </header>
        {currentVehicle &&
          currentVehicle.creation_status === "active" &&
          canManageLifecycle &&
          writable && (
            <div className="vehicle-lifecycle-actions">
              <div>
                <strong>Ações da viatura</strong>
                <span>
                  {reservationBlocksLifecycle
                    ? "A reserva ativa deve ser libertada antes de pausar, arquivar ou eliminar."
                    : agreement
                      ? "O acordo de consignação é preservado; esta viatura não pode ser eliminada."
                      : currentVehicle.acquisition_type === "trade_in"
                        ? "A retoma está ligada à venda de origem e não pode ser eliminada."
                        : "O sistema verifica processos ativos e preserva a auditoria."}
                </span>
              </div>
              <div className="vehicle-lifecycle-control">
                <label>
                  <span>O que pretende fazer?</span>
                  <select
                    value={lifecycleChoice}
                    onChange={(event) => setLifecycleChoice(event.target.value)}
                    disabled={saving || reservationBlocksLifecycle}
                  >
                    <option value="">Escolher uma ação</option>
                    {saleStatuses.has(currentVehicle.stock_status) ? (
                      <option value="reverse">Reverter venda</option>
                    ) : currentVehicle.stock_status === "paused" ||
                      currentVehicle.stock_status === "archived" ? (
                      <option value="reactivate">Reativar viatura</option>
                    ) : (
                      <>
                        <option value="pause">Pausar viatura</option>
                        <option value="archive">Arquivar viatura</option>
                      </>
                    )}
                  </select>
                </label>
                <button
                  type="button"
                  onClick={() => void applyLifecycleChoice()}
                  disabled={
                    saving || !lifecycleChoice || reservationBlocksLifecycle
                  }
                >
                  Aplicar alteração
                </button>
                <button
                  type="button"
                  className="danger"
                  onClick={deleteVehicle}
                  disabled={
                    saving || !consignmentReady || deletionBlockedByKnownProcess
                  }
                >
                  Eliminar
                </button>
              </div>
            </div>
          )}
        <form ref={vehicleFormRef} onSubmit={submit} onChange={() => setFormDirty(true)} noValidate>
          {notice && (
            <button
              type="button"
              className="stock-notice"
              onClick={() => setNotice(null)}
            >
              {notice} ×
            </button>
          )}
          <div className="field-guide">
            <strong>Como preencher</strong>
            <span>
              <b>Obrigatório</b> para guardar
            </span>
            <span>
              <b>Recomendado</b> para anunciar e vender
            </span>
            <span>Restantes campos são opcionais</span>
          </div>
          <nav
            className="vehicle-profile-tabs"
            aria-label="Categorias da ficha da viatura"
          >
            <button
              type="button"
              className={profileTab === "general" ? "active" : ""}
              onClick={() => setProfileTab("general")}
            >
              Principal
            </button>
            <button
              type="button"
              className={profileTab === "technical" ? "active" : ""}
              onClick={() => setProfileTab("technical")}
            >
              Características
            </button>
            <button
              type="button"
              className={profileTab === "equipment" ? "active" : ""}
              onClick={() => setProfileTab("equipment")}
            >
              Equipamento
            </button>
            <button
              type="button"
              className={profileTab === "media" ? "active" : ""}
              onClick={() => setProfileTab("media")}
            >
              Fotografias
            </button>
            {currentVehicle && (
              <button type="button" className={profileTab === "documents" ? "active" : ""} onClick={() => setProfileTab("documents")}>
                Documentos
              </button>
            )}
            {currentVehicle?.creation_status === "active" && (
              <button
                type="button"
                className={profileTab === "marketing" ? "active" : ""}
                onClick={() => setProfileTab("marketing")}
              >
                Marketing
              </button>
            )}
            {currentVehicle &&
              acquisitionType === "consignment" && (agreement || consignmentConfigured) && (
                <button
                  type="button"
                  className={profileTab === "consignment" ? "active" : ""}
                  onClick={() => setProfileTab("consignment")}
                >
                  Consignado
                </button>
              )}
            {currentVehicle?.creation_status === "active" && (
              <button
                type="button"
                className={profileTab === "commercial" ? "active" : ""}
                onClick={() => setProfileTab("commercial")}
              >
                Comercial
              </button>
            )}
            {currentVehicle?.creation_status === "active" && (
              <button
                type="button"
                className={profileTab === "chronology" ? "active" : ""}
                onClick={() => setProfileTab("chronology")}
              >
                Histórico
              </button>
            )}
          </nav>
          <div
            className="vehicle-profile-panel general-panel"
            hidden={profileTab !== "general" && !consignmentDialogOpen}
          >
            {false && <fieldset className="stock-section entry-section">
              <legend>Entrada e acompanhamento</legend>
              <p className="stock-section-help">
                A origem não muda o estado. A etapa indica onde a viatura está
                fisicamente no processo.
              </p>
              <div className="stock-form-grid">
                <label>
                  <span>Tipo de entrada</span>
                  <select
                    name="acquisition_type"
                    value={acquisitionType}
                    onChange={(e) => {
                      const nextType = e.target.value;
                      setAcquisitionType(nextType);
                      if (nextType === "trade_in" && !vehicle)
                        setRecordStatus("incomplete");
                    }}
                    disabled={!writable || (!!agreement && !canManageLifecycle)}
                  >
                    {Object.entries(acquisitionLabels).map(([key, label]) => (
                      <option
                        value={key}
                        key={key}
                        disabled={
                          key === "consignment" &&
                          !canManageLifecycle &&
                          vehicle?.acquisition_type !== "consignment"
                        }
                      >
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Etapa operacional</span>
                  <select
                    name="operational_stage"
                    defaultValue={vehicle?.operational_stage ?? "reception"}
                    disabled={!writable}
                  >
                    {Object.entries(stageLabels).map(([key, label]) => (
                      <option value={key} key={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Estado da viatura</span>
                  <select
                    name="stock_status"
                    value={chosenStatus}
                    onChange={(e) => setChosenStatus(e.target.value)}
                    disabled
                  >
                    {Object.entries(stockLabels).map(([key, label]) => (
                      <option
                        value={key}
                        key={key}
                        disabled={
                          (saleStatuses.has(key) &&
                            key !== vehicle?.stock_status) ||
                          ((key === "paused" || key === "archived") &&
                            !canManageLifecycle)
                        }
                      >
                        {label}
                      </option>
                    ))}
                  </select>
                  <small>
                    Reservas, vendas, pausas e arquivo são alterados pelas ações
                    próprias e ficam auditados.
                  </small>
                </label>
                <label>
                  <span>Estado automático do cadastro</span>
                  <select
                    name="record_status"
                    value={recordStatus}
                    onChange={(event) =>
                      setRecordStatus(
                        event.target.value as "incomplete" | "complete",
                      )
                    }
                    disabled={!writable}
                  >
                    <option value="incomplete">
                      Incompleta — não publicar
                    </option>
                    <option value="complete">Completa</option>
                  </select>
                  <small>
                    As retomas entram incompletas. Só podem ser publicadas
                    depois de confirmar a ficha como completa.
                  </small>
                </label>
                {chosenStatus === "paused" && (
                  <label className="wide">
                    <span>Motivo da pausa *</span>
                    <textarea
                      name="pause_reason"
                      rows={2}
                      defaultValue={vehicle?.pause_reason ?? ""}
                      required
                      disabled={!writable}
                    />
                  </label>
                )}
              </div>
            </fieldset>}
            {acquisitionType === "consignment" && consignmentDialogOpen && (
              <div className="consignment-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="consignment-title">
              <section className="consignment-modal">
                <header>
                  <div><p className="eyebrow">Acordo da viatura</p><h2 id="consignment-title">Consignação</h2><p>Identifique o proprietário e registe todas as condições acordadas.</p></div>
                  <button type="button" aria-label="Fechar" onClick={() => { setConsignmentDialogOpen(false); if (!agreement) setAcquisitionType("owned"); }}>×</button>
                </header>
              <fieldset className="stock-section consignment-section">
                <legend>Consignação</legend>
                <p className="stock-section-help">
                  Identifique o proprietário e as condições acordadas. O mesmo
                  proprietário pode ficar associado a várias viaturas.
                </p>
                <div className="consignment-remuneration-guide">
                  <strong>Como o stand será remunerado?</strong>
                  <p><b>Margem:</b> o proprietário recebe o valor acordado e o stand fica com a diferença até ao preço final de venda.</p>
                  <p><b>Comissão fixa:</b> o stand recebe um valor monetário previamente definido.</p>
                  <p><b>Comissão percentual:</b> o stand recebe a percentagem indicada sobre o preço final de venda.</p>
                </div>
                {!canManageLifecycle && (
                  <p className="consignment-permission-note">
                    Apenas o Master ou Gestor pode alterar condições de
                    consignação.
                  </p>
                )}
                {!consignmentReady ? (
                  <p className="stock-section-help">A carregar o acordo…</p>
                ) : (
                  <div className="stock-form-grid">
                    <label className="wide">
                      <span>Cliente proprietário *</span>
                      <select
                        name="owner_id"
                        value={ownerId}
                        onChange={(e) => setOwnerId(e.target.value)}
                        required
                        disabled={!writable || !canManageLifecycle}
                      >
                        <option value="" disabled>
                          Escolha um cliente
                        </option>
                        <option value="__new__">
                          + Cadastrar novo cliente
                        </option>
                        {owners.map((owner) => (
                          <option key={owner.id} value={owner.id}>
                            {owner.display_name}
                            {owner.tax_identifier ? ` · ${owner.tax_identifier}` : ""}
                          </option>
                        ))}
                      </select>
                    </label>
                    {ownerId === "__new__" && (
                      <>
                        <div className="wide stock-inline-form-heading">
                          <strong>Novo cliente</strong>
                          <span>Este cadastro ficará disponível também no menu Clientes.</span>
                        </div>
                        <label>
                          <span>Tipo de pessoa *</span>
                          <select
                            name="owner_type"
                            value={newOwnerKind}
                            onChange={(event) => setNewOwnerKind(event.target.value as CustomerKind)}
                            disabled={!writable || !canManageLifecycle}
                          >
                            <option value="individual">Pessoa singular</option>
                            <option value="company">Empresa</option>
                          </select>
                        </label>
                        <label>
                          <span>Tipo de cliente *</span>
                          <select
                            name="customer_role"
                            defaultValue="owner"
                            required
                            disabled={!writable || !canManageLifecycle}
                          >
                            <option value="buyer">Comprador</option>
                            <option value="owner">Proprietário</option>
                            <option value="both">Comprador e proprietário</option>
                          </select>
                          <small>Para esta consignação, escolha Proprietário ou Ambos.</small>
                        </label>
                        <label>
                          <span>{newOwnerKind === "company" ? "Pessoa de contacto *" : "Nome completo *"}</span>
                          <input
                            name="owner_name"
                            required
                            disabled={!writable || !canManageLifecycle}
                          />
                        </label>
                        {newOwnerKind === "company" && <><label><span>Nome legal da empresa *</span><input name="owner_legal_name" required disabled={!writable || !canManageLifecycle} /></label><label><span>Marca / nome comercial *</span><input name="owner_trade_name" required disabled={!writable || !canManageLifecycle} /></label></>}
                        <label>
                          <span>{currencyCode === "BRL" ? (newOwnerKind === "company" ? "CNPJ" : "CPF") : (newOwnerKind === "company" ? "NIPC" : "NIF")}</span>
                          <input
                            name="owner_tax_id"
                            inputMode="numeric"
                            maxLength={currencyCode === "BRL" ? (newOwnerKind === "company" ? 18 : 14) : 9}
                            onInput={(event) => { event.currentTarget.value = formatTaxIdentifier(event.currentTarget.value, currencyCode === "BRL" ? "BR" : "PT", newOwnerKind); }}
                            disabled={!writable || !canManageLifecycle}
                          />
                        </label>
                        <label>
                          <span>Telefone</span>
                          <input
                            name="owner_phone"
                            disabled={!writable || !canManageLifecycle}
                          />
                        </label>
                        <label>
                          <span>Email</span>
                          <input
                            name="owner_email"
                            type="email"
                            maxLength={254}
                            placeholder="nome@empresa.pt"
                            disabled={!writable || !canManageLifecycle}
                          />
                        </label>
                        <label>
                          <span>Morada</span>
                          <input
                            name="owner_address"
                            disabled={!writable || !canManageLifecycle}
                          />
                        </label>
                        <label className="wide">
                          <span>Notas do cliente</span>
                          <textarea
                            name="owner_notes"
                            rows={2}
                            disabled={!writable || !canManageLifecycle}
                          />
                        </label>
                      </>
                    )}
                    <label>
                      <span>Estado do acordo *</span>
                      <select
                        name="agreement_status"
                        defaultValue={agreement?.status ?? "active"}
                        disabled={!writable || !canManageLifecycle}
                      >
                        <option value="draft">Rascunho</option>
                        <option value="active">Ativo</option>
                        <option value="ended">Terminado</option>
                        <option value="cancelled">Cancelado</option>
                      </select>
                    </label>
                    <label>
                      <span>Valor devido ao proprietário *</span>
                      <CurrencyInput currencyCode={currencyCode} name="owner_amount" type="number" min="0" step="0.01" required defaultValue={agreement ? agreement.owner_amount_cents / 100 : ""} disabled={!writable || !canManageLifecycle}/>
                      <small>Montante que o stand deverá entregar ao proprietário quando a viatura for vendida.</small>
                    </label>
                    <label>
                      <span>Forma de remuneração do stand</span>
                      <select
                        name="commission_type"
                        value={commissionType}
                        onChange={(event) => setCommissionType(event.target.value)}
                        disabled={!writable || !canManageLifecycle}
                      >
                        <option value="margin">
                          Margem entre valor devido e venda
                        </option>
                        <option value="fixed">Comissão fixa</option>
                        <option value="percentage">Comissão percentual</option>
                      </select>
                      <small>{commissionType === "margin" ? "O stand recebe a diferença entre o preço final de venda e o valor devido ao proprietário." : commissionType === "fixed" ? "O stand recebe um valor monetário fixo pela venda." : "O stand recebe uma percentagem do preço final de venda."}</small>
                    </label>
                    {commissionType !== "margin" && <label>
                      <span>{commissionType === "fixed" ? "Comissão fixa do stand" : "Comissão percentual do stand"}</span>
                      <CurrencyInput currencyCode={currencyCode} symbol={commissionType === "percentage" ? "%" : undefined} name="commission_value" type="number" min="0" max={commissionType === "percentage" ? 100 : undefined} step="0.01" defaultValue={agreement?.commission_value ?? ""} disabled={!writable || !canManageLifecycle}/>
                      <small>{commissionType === "fixed" ? "Valor único que remunera o stand." : "Percentagem aplicada ao preço final de venda."}</small>
                    </label>}
                    <label>
                      <span>Preço mínimo autorizado</span>
                      <CurrencyInput currencyCode={currencyCode} name="minimum_sale_price" type="number" min="0" step="0.01" defaultValue={agreement?.minimum_sale_price_cents != null ? agreement.minimum_sale_price_cents / 100 : ""} disabled={!writable || !canManageLifecycle}/>
                    </label>
                    <label>
                      <span>Data de início *</span>
                      <input
                        name="starts_on"
                        type="date"
                        required
                        defaultValue={
                          agreement?.starts_on ??
                          new Date().toISOString().slice(0, 10)
                        }
                        disabled={!writable || !canManageLifecycle}
                      />
                    </label>
                    <label>
                      <span>Data de fim</span>
                      <input
                        name="ends_on"
                        type="date"
                        defaultValue={agreement?.ends_on ?? ""}
                        disabled={!writable || !canManageLifecycle}
                      />
                    </label>
                    <label className="wide">
                      <span>Condições acordadas</span>
                      <textarea
                        name="terms"
                        rows={3}
                        defaultValue={agreement?.terms ?? ""}
                        disabled={!writable || !canManageLifecycle}
                      />
                    </label>
                    <label className="wide">
                      <span>Notas internas do acordo</span>
                      <textarea
                        name="consignment_notes"
                        rows={2}
                        defaultValue={agreement?.internal_notes ?? ""}
                        disabled={!writable || !canManageLifecycle}
                      />
                    </label>
                  </div>
                )}
              </fieldset>
              <footer>
                <button type="button" className="stock-secondary" onClick={() => { setConsignmentDialogOpen(false); if (!agreement) setAcquisitionType("owned"); }}>Cancelar</button>
                <button type="button" className="stock-primary" onClick={(event) => {
                  if (!ownerId) { setError("Escolha o cliente proprietário da viatura consignada."); return; }
                  const form = event.currentTarget.form;
                  const valueOf = (name: string) => (form?.elements.namedItem(name) as HTMLInputElement | null)?.value.trim() ?? "";
                  if (ownerId === "__new__" && !valueOf("owner_name")) { setError("Indique o nome do novo cliente proprietário."); return; }
                  if (!valueOf("owner_amount")) { setError("Indique o valor devido ao proprietário."); return; }
                  if (!valueOf("starts_on")) { setError("Indique a data de início do acordo."); return; }
                  setConsignmentConfigured(true);
                  setNotice("Consignação validada. A guardar a viatura e o acordo…");
                  window.setTimeout(() => form?.requestSubmit(), 0);
                }}>Guardar consignação</button>
              </footer>
              </section>
              </div>
            )}
            <fieldset className="stock-section identity-section">
              <legend>Identificação</legend>
              {!vehicle && countryCode === "PT" && currencyCode === "EUR" && (
                <div className="vehicle-identification-mode" role="group" aria-label="Modo de preenchimento da viatura">
                  <button type="button" className={identificationMode === "registration" ? "is-active" : ""} aria-pressed={identificationMode === "registration"} onClick={() => setIdentificationMode("registration")} disabled={!writable}>
                    Preencher por matrícula
                  </button>
                  <button type="button" className={identificationMode === "manual" ? "is-active" : ""} aria-pressed={identificationMode === "manual"} onClick={() => { setIdentificationMode("manual"); setIdentifyingVehicle(false); }} disabled={!writable}>
                    Preencher manualmente
                  </button>
                </div>
              )}
              <p className="stock-section-help">
                Matrícula, marca e modelo são obrigatórios. Ano e quilometragem
                são recomendados para identificar e publicar corretamente a
                viatura.
              </p>
              <div className="stock-form-grid">
                {useStockReference && (
                  <label>
                    <span>
                      Referência de stock{stockReferenceRequired ? " *" : ""}
                    </span>
                    <input
                      name="stock_number"
                      defaultValue={vehicle?.stock_number ?? ""}
                      required={stockReferenceRequired}
                      disabled={!writable}
                    />
                  </label>
                )}
                <label>
                  <span>Matrícula *</span>
                  <input
                    name="registration"
                    defaultValue={countryCode === "PT" && currencyCode === "EUR" ? formatPortugueseRegistration(vehicle?.registration ?? "") : vehicle?.registration ?? ""}
                    onInput={(event) => {
                      if (countryCode === "PT" && currencyCode === "EUR") {
                        event.currentTarget.value = formatPortugueseRegistration(event.currentTarget.value);
                        if (lookupTimerRef.current) window.clearTimeout(lookupTimerRef.current);
                        if (identificationMode === "registration" && event.currentTarget.value.length === 8) {
                          const registration = event.currentTarget.value;
                          lookupTimerRef.current = window.setTimeout(() => void identifyVehicle(registration), 250);
                        }
                      }
                    }}
                    maxLength={countryCode === "PT" && currencyCode === "EUR" ? 8 : undefined}
                    pattern={countryCode === "PT" && currencyCode === "EUR" ? "[A-Za-z0-9]{2}-[A-Za-z0-9]{2}-[A-Za-z0-9]{2}" : undefined}
                    placeholder={countryCode === "PT" && currencyCode === "EUR" ? "XX-XX-XX" : undefined}
                    autoCapitalize="characters"
                    required
                    disabled={!writable}
                  />
                  {identifyingVehicle && <small className="vehicle-identification-progress">A preencher os dados da viatura…</small>}
                </label>
                <label>
                  <span>VIN / número de chassis</span>
                  <input
                    name="vin"
                    minLength={17}
                    maxLength={17}
                    defaultValue={vehicle?.vin ?? ""}
                    disabled={!writable}
                    placeholder="17 caracteres"
                  />
                </label>
                <label>
                  <span>Marca *</span>
                  <select
                    name="make"
                    defaultValue={vehicle?.make ?? ""}
                    required
                    disabled={!writable}
                  >
                    <option value="">Escolher marca</option>
                    {normalizeVehicleBrands([
                      ...vehicleBrands,
                      ...(vehicle?.make ? [vehicle.make] : []),
                    ]).map((brand) => (
                      <option key={brand} value={brand}>
                        {brand}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Modelo *</span>
                  <input
                    name="model"
                    defaultValue={vehicle?.model}
                    required
                    disabled={!writable}
                  />
                </label>
                <label className="wide">
                  <span>Versão</span>
                  <input
                    name="version"
                    defaultValue={vehicle?.version ?? ""}
                    disabled={!writable}
                  />
                </label>
                <label>
                  <span>Ano da primeira matrícula</span>
                  <input
                    name="year"
                    type="number"
                    min="1900"
                    max="2100"
                    defaultValue={vehicle?.first_registration_year ?? ""}
                    disabled={!writable}
                  />
                </label>
                <label>
                  <span>Data da primeira matrícula</span>
                  <input
                    name="registration_date"
                    type="date"
                    defaultValue={vehicle?.registration_date ?? ""}
                    disabled={!writable}
                  />
                </label>
                <label>
                  <span>Quilómetros</span>
                  <input
                    name="mileage"
                    type="number"
                    min="0"
                    defaultValue={vehicle?.mileage_km ?? 0}
                    disabled={!writable}
                  />
                </label>
                <label>
                  <span>Combustível</span>
                  <select
                    name="fuel_type"
                    value={fuelType}
                    onChange={(event) => setFuelType(event.target.value)}
                    disabled={!writable}
                  >
                    <option value="">Escolher</option>
                    {[
                      "Gasolina",
                      "Diesel",
                      "Híbrido",
                      "Híbrido plug-in",
                      "Elétrico",
                      "GPL",
                    ].map((x) => (
                      <option key={x}>{x}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Transmissão</span>
                  <select
                    name="transmission"
                    defaultValue={vehicle?.transmission ?? ""}
                    disabled={!writable}
                  >
                    <option value="">Escolher</option>
                    <option>Manual</option>
                    <option>Automática</option>
                  </select>
                </label>
                <div className="stock-color-fields">
                  <label>
                    <span>Cor</span>
                    <select name="color" defaultValue={splitVehicleColor(vehicle?.color).color} disabled={!writable}>
                      <option value="">Escolher cor</option>
                      {splitVehicleColor(vehicle?.color).color && !vehicleColors.includes(splitVehicleColor(vehicle?.color).color) && <option value={splitVehicleColor(vehicle?.color).color}>{splitVehicleColor(vehicle?.color).color}</option>}
                      {vehicleColors.map((color) => <option value={color} key={color}>{color}</option>)}
                    </select>
                  </label>
                  <label>
                    <span>Tipo de pintura</span>
                    <select name="paint_finish" defaultValue={vehicle?.paint_finish ?? splitVehicleColor(vehicle?.color).paintFinish} disabled={!writable}>
                      <option value="">Escolher acabamento</option>
                      {Object.entries(paintFinishLabels).map(([value, label]) => <option value={value} key={value}>{label}</option>)}
                    </select>
                  </label>
                </div>
                <label>
                  <span>Carroçaria</span>
                  <select
                    name="body_type"
                    defaultValue={vehicle?.body_type ?? ""}
                    disabled={!writable}
                  >
                    <option value="">Selecionar</option>
                    {bodyTypes.map((type) => (
                      <option value={type} key={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>País do primeiro registo</span>
                  <small className="field-help">Permite identificar a viatura como “Nacional”. Se não souber, deixe por preencher.</small>
                  <select name="first_registration_country_code" defaultValue={vehicle?.first_registration_country_code??""} disabled={!writable}>
                    <option value="">Não informado</option><option value="PT">Portugal</option><option value="BR">Brasil</option><option value="ES">Espanha</option><option value="FR">França</option><option value="DE">Alemanha</option><option value="BE">Bélgica</option><option value="NL">Países Baixos</option><option value="IT">Itália</option><option value="LU">Luxemburgo</option><option value="CH">Suíça</option><option value="AT">Áustria</option><option value="IE">Irlanda</option><option value="GB">Reino Unido</option><option value="PL">Polónia</option><option value="CZ">Chéquia</option><option value="DK">Dinamarca</option><option value="SE">Suécia</option><option value="NO">Noruega</option><option value="FI">Finlândia</option><option value="US">Estados Unidos</option><option value="CA">Canadá</option><option value="JP">Japão</option><option value="KR">Coreia do Sul</option>
                  </select>
                </label>
                <label>
                  <span>Cilindrada (cm³)</span>
                  <input
                    name="engine_capacity_cc"
                    type="number"
                    min="1"
                    max="20000"
                    defaultValue={vehicle?.engine_capacity_cc ?? ""}
                    disabled={!writable}
                  />
                </label>
                <label>
                  <span>Potência (kW)</span>
                  <input
                    name="power_kw"
                    type="number"
                    min="1"
                    max="2000"
                    defaultValue={vehicle?.power_kw ?? ""}
                    disabled={!writable}
                  />
                </label>
                <label>
                  <span>Portas</span>
                  <input
                    name="doors"
                    type="number"
                    min="1"
                    max="10"
                    defaultValue={vehicle?.doors ?? ""}
                    disabled={!writable}
                  />
                </label>
                <label>
                  <span>Lugares</span>
                  <input
                    name="seats"
                    type="number"
                    min="1"
                    max="100"
                    defaultValue={vehicle?.seats ?? ""}
                    disabled={!writable}
                  />
                </label>
              </div>
            </fieldset>
          </div>
          <div
            className="vehicle-profile-panel"
            hidden={profileTab !== "technical"}
          >
            <fieldset className="stock-section">
              <legend>Características</legend>
              <p className="stock-section-help">
                Complete os dados técnicos, o histórico de manutenção e, quando
                aplicável, a bateria e o carregamento.
              </p>
              <div className="stock-form-grid">
                <label>
                  <span>Número de velocidades</span>
                  <input
                    name="gears"
                    type="number"
                    min="1"
                    max="20"
                    defaultValue={specification?.gears ?? ""}
                    disabled={!writable}
                  />
                </label>
                <label>
                  <span>Tração</span>
                  <select
                    key={specification?.traction ?? "empty"}
                    name="traction"
                    defaultValue={specification?.traction ?? ""}
                    disabled={!writable}
                  >
                    <option value="">Escolher</option>
                    <option value="front">Dianteira</option>
                    <option value="rear">Traseira</option>
                    <option value="all_wheel">Integral</option>
                  </select>
                </label>
                <label>
                  <span>Emissões de CO₂ (g/km)</span>
                  <input
                    name="co2_g_km"
                    type="number"
                    min="0"
                    step="0.01"
                    defaultValue={specification?.co2_g_km ?? ""}
                    disabled={!writable}
                  />
                </label>
                <label>
                  <span>Número de proprietários anteriores</span>
                  <input
                    name="previous_owners"
                    type="number"
                    min="0"
                    defaultValue={specification?.previous_owners ?? ""}
                    disabled={!writable}
                  />
                </label>
                <label>
                  <span>Inspeção válida até</span>
                  <input
                    name="inspection_until"
                    type="date"
                    defaultValue={specification?.inspection_until ?? ""}
                    disabled={!writable}
                  />
                </label>
                <label>
                  <span>Garantia de fábrica até</span>
                  <input
                    name="factory_warranty_until"
                    type="date"
                    defaultValue={specification?.factory_warranty_until ?? ""}
                    disabled={!writable}
                  />
                </label>
                <label>
                  <span>Limite da garantia (km)</span>
                  <input
                    name="factory_warranty_km"
                    type="number"
                    min="0"
                    defaultValue={specification?.factory_warranty_km ?? ""}
                    disabled={!writable}
                  />
                </label>
                <div className="stock-check-group wide">
                  <label>
                    <input
                      name="full_service_history"
                      type="checkbox"
                      defaultChecked={specification?.full_service_history}
                      disabled={!writable}
                    />
                    <span>Histórico de manutenção completo</span>
                  </label>
                  <label>
                    <input
                      name="second_key"
                      type="checkbox"
                      defaultChecked={specification?.second_key}
                      disabled={!writable}
                    />
                    <span>Segunda chave</span>
                  </label>
                  <label>
                    <input
                      name="non_smoker"
                      type="checkbox"
                      defaultChecked={specification?.non_smoker}
                      disabled={!writable}
                    />
                    <span>Não fumador</span>
                  </label>
                  <label>
                    <input
                      name="classic_vehicle"
                      type="checkbox"
                      defaultChecked={specification?.classic_vehicle}
                      disabled={!writable}
                    />
                    <span>Viatura clássica</span>
                  </label>
                </div>
              </div>
            </fieldset>
            <fieldset className="stock-section">
              <legend>Manutenção</legend>
              <div className="stock-form-grid">
                <label>
                  <span>Última manutenção</span>
                  <input
                    name="last_service_on"
                    type="date"
                    defaultValue={specification?.last_service_on ?? ""}
                    disabled={!writable}
                  />
                </label>
                <label>
                  <span>Quilómetros na manutenção</span>
                  <input
                    name="last_service_km"
                    type="number"
                    min="0"
                    defaultValue={specification?.last_service_km ?? ""}
                    disabled={!writable}
                  />
                </label>
                <label>
                  <span>Intervalo de manutenção (meses)</span>
                  <input
                    name="service_interval_months"
                    type="number"
                    min="1"
                    defaultValue={specification?.service_interval_months ?? ""}
                    disabled={!writable}
                  />
                </label>
                <label>
                  <span>Intervalo de manutenção (km)</span>
                  <input
                    name="service_interval_km"
                    type="number"
                    min="1"
                    defaultValue={specification?.service_interval_km ?? ""}
                    disabled={!writable}
                  />
                </label>
                <label>
                  <span>Substituição da correia</span>
                  <input
                    name="timing_belt_on"
                    type="date"
                    defaultValue={specification?.timing_belt_on ?? ""}
                    disabled={!writable}
                  />
                </label>
                <label>
                  <span>Quilómetros na substituição</span>
                  <input
                    name="timing_belt_km"
                    type="number"
                    min="0"
                    defaultValue={specification?.timing_belt_km ?? ""}
                    disabled={!writable}
                  />
                </label>
              </div>
            </fieldset>
            {(fuelType.includes("Elétrico") ||
              fuelType.includes("Híbrido")) && (
              <fieldset className="stock-section">
                <legend>Bateria e carregamento</legend>
                <div className="stock-form-grid">
                  <label>
                    <span>Capacidade da bateria (kWh)</span>
                    <input
                      name="battery_capacity_kwh"
                      type="number"
                      min="0.01"
                      step="0.01"
                      defaultValue={specification?.battery_capacity_kwh ?? ""}
                      disabled={!writable}
                    />
                  </label>
                  <label>
                    <span>Estado da bateria (%)</span>
                    <input
                      name="battery_health_percent"
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      defaultValue={specification?.battery_health_percent ?? ""}
                      disabled={!writable}
                    />
                  </label>
                  <label>
                    <span>Autonomia elétrica (km)</span>
                    <input
                      name="electric_range_km"
                      type="number"
                      min="0"
                      defaultValue={specification?.electric_range_km ?? ""}
                      disabled={!writable}
                    />
                  </label>
                  <label>
                    <span>Consumo (kWh/100 km)</span>
                    <input
                      name="consumption_kwh_100km"
                      type="number"
                      min="0.01"
                      step="0.01"
                      defaultValue={specification?.consumption_kwh_100km ?? ""}
                      disabled={!writable}
                    />
                  </label>
                  <label>
                    <span>Conector de carregamento</span>
                    <input
                      name="charging_connector"
                      defaultValue={specification?.charging_connector ?? ""}
                      disabled={!writable}
                    />
                  </label>
                  <label>
                    <span>Potência máxima de carga (kW)</span>
                    <input
                      name="max_charge_kw"
                      type="number"
                      min="0.01"
                      step="0.01"
                      defaultValue={specification?.max_charge_kw ?? ""}
                      disabled={!writable}
                    />
                  </label>
                </div>
              </fieldset>
            )}
          </div>
          <div
            className="vehicle-profile-panel"
            hidden={profileTab !== "equipment"}
          >
            <fieldset className="stock-section equipment-section">
              <legend>Equipamentos</legend>
              <div className="equipment-toolbar">
                <input
                  value={equipmentSearch}
                  onChange={(event) => setEquipmentSearch(event.target.value)}
                  placeholder="Pesquisar equipamento"
                />
                <strong>{selectedEquipment.length} selecionados</strong>
              </div>
              <div className="equipment-categories">
                {Object.entries(equipmentCategoryLabels).map(
                  ([category, label]) => {
                    const items = equipment.filter(
                      (item) =>
                        item.category === category &&
                        item.name
                          .toLocaleLowerCase("pt-PT")
                          .includes(equipmentSearch.toLocaleLowerCase("pt-PT")),
                    );
                    return (
                      <section key={category}>
                        <h3>
                          <span>{label}</span>
                          {items.length > 0 && writable && (
                            <button
                              type="button"
                              onClick={() => {
                                const ids = items.map((item) => item.id);
                                const allSelected = ids.every((id) => selectedEquipment.includes(id));
                                setSelectedEquipment((current) =>
                                  allSelected
                                    ? current.filter((id) => !ids.includes(id))
                                    : Array.from(new Set([...current, ...ids])),
                                );
                              }}
                            >
                              {items.every((item) => selectedEquipment.includes(item.id))
                                ? "Desmarcar todos"
                                : "Marcar todos"}
                            </button>
                          )}
                        </h3>
                        <div>
                          {items.map((item) => (
                            <label key={item.id}>
                              <input
                                type="checkbox"
                                checked={selectedEquipment.includes(item.id)}
                                onChange={(event) =>
                                  setSelectedEquipment((current) =>
                                    event.target.checked
                                      ? [...current, item.id]
                                      : current.filter((id) => id !== item.id),
                                  )
                                }
                                disabled={!writable}
                              />
                              <span>{item.name}</span>
                              {item.organization_id && <small>Do stand</small>}
                            </label>
                          ))}
                        </div>
                      </section>
                    );
                  },
                )}
              </div>
              {writable && (
                <div className="equipment-custom">
                  <strong>Adicionar equipamento do stand</strong>
                  <select
                    value={customEquipmentCategory}
                    onChange={(event) =>
                      setCustomEquipmentCategory(event.target.value)
                    }
                  >
                    {Object.entries(equipmentCategoryLabels).map(
                      ([key, label]) => (
                        <option key={key} value={key}>
                          {label}
                        </option>
                      ),
                    )}
                  </select>
                  <input
                    value={customEquipmentName}
                    onChange={(event) =>
                      setCustomEquipmentName(event.target.value)
                    }
                    placeholder="Nome do equipamento"
                  />
                  <button
                    type="button"
                    className="stock-secondary"
                    onClick={() => void addCustomEquipment()}
                    disabled={!customEquipmentName.trim()}
                  >
                    Adicionar
                  </button>
                </div>
              )}
            </fieldset>
          </div>
          <div
            className="vehicle-profile-panel"
            hidden={profileTab !== "media"}
          >
            {currentVehicle ? (
              <VehicleMediaPanel
                organizationId={organizationId}
                vehicleId={currentVehicle.id}
                writable={writable && canManageLifecycle}
                initialFiles={pendingPhotos}
                initialDocuments={pendingDocuments}
                onInitialFilesHandled={() => setPendingPhotos([])}
                onInitialDocumentsHandled={() => setPendingDocuments([])}
                mode="photos"
              />
            ) : (
              <>
                <PendingVehiclePhotos
                  files={pendingPhotos}
                  onChange={setPendingPhotos}
                  maxPhotos={maxVehiclePhotos}
                />
                <PendingVehicleDocuments
                  documents={pendingDocuments}
                  onChange={setPendingDocuments}
                />
              </>
            )}
          </div>
          {currentVehicle && (
            <div className="vehicle-profile-panel" hidden={profileTab !== "documents"}>
              <VehicleMediaPanel organizationId={organizationId} vehicleId={currentVehicle.id} writable={writable && canManageLifecycle} mode="documents" />
            </div>
          )}
          <div
            className="vehicle-profile-panel"
            hidden={profileTab !== "general"}
          >
            <fieldset className="stock-section entry-section">
              <legend>Entrada e acompanhamento</legend>
              <p className="stock-section-help">A origem não muda o estado. A etapa indica onde a viatura está fisicamente no processo.</p>
              <div className="stock-form-grid">
                <label><span>Tipo de entrada</span><select name="acquisition_type" value={acquisitionType} onChange={(event) => { const next = event.target.value; setAcquisitionType(next); if (next === "consignment") setConsignmentDialogOpen(true); if (next === "trade_in" && !vehicle) setRecordStatus("incomplete"); }} disabled={!writable || acquisitionType === "consignment" && Boolean(agreement)}>{Object.entries(acquisitionLabels).map(([key,label]) => <option value={key} key={key} disabled={key === "consignment" && !canManageLifecycle && vehicle?.acquisition_type !== "consignment"}>{label}</option>)}</select></label>
                <label><span>Etapa operacional</span><select name="operational_stage" defaultValue={vehicle?.operational_stage ?? "reception"} disabled={!writable}>{Object.entries(stageLabels).map(([key,label]) => <option value={key} key={key}>{label}</option>)}</select></label>
                <label><span>Estado da viatura</span><select name="stock_status" value={chosenStatus} disabled>{Object.entries(stockLabels).map(([key,label]) => <option value={key} key={key}>{label}</option>)}</select><small>Reservas, vendas, pausas e arquivo são alterados pelas ações próprias e ficam auditados.</small></label>
                {currentVehicle?.record_status === "incomplete" && <div className="vehicle-incomplete-status" role="status"><strong>Cadastro incompleto</strong><small>Preencha os dados obrigatórios para disponibilizar a publicação.</small></div>}
                {chosenStatus === "paused" && <label className="wide"><span>Motivo da pausa *</span><textarea name="pause_reason" rows={2} defaultValue={vehicle?.pause_reason ?? ""} required disabled={!writable}/></label>}
              </div>
            </fieldset>
            <fieldset className="stock-section">
              <legend>Dados administrativos</legend>
              <p className="stock-section-help">
                Informação interna de aquisição e localização. Estes dados não
                são publicados no site.
              </p>
              <div className="stock-form-grid">
                <label>
                  <span>Data de aquisição</span>
                  <input
                    name="purchase_date"
                    type="date"
                    defaultValue={vehicle?.purchase_date ?? ""}
                    disabled={!writable}
                  />
                </label>
                <label>
                  <span>Fornecedor ou origem</span>
                  <input
                    name="supplier_name"
                    defaultValue={vehicle?.supplier_name ?? ""}
                    disabled={!writable}
                  />
                </label>
                <label>
                  <span>Localização no stock</span>
                  <select
                    name="branch_id"
                    defaultValue={
                      vehicle?.branch_id ??
                      stockBranches.find(
                        (branch) =>
                          branch.name.localeCompare(vehicle?.stock_location ?? "", undefined, {
                            sensitivity: "accent",
                          }) === 0,
                      )?.id ??
                      ""
                    }
                    disabled={!writable}
                  >
                    <option value="">Escolher filial</option>
                    {stockBranches.map((branch) => (
                      <option key={branch.id} value={branch.id}>{branch.name}{branch.is_headquarters ? " · Sede" : ""}</option>
                    ))}
                  </select>
                  <small>Filiais disponíveis em Configurações → Administração.</small>
                </label>
                <label>
                  <span>Garantia prevista</span>
                  <select
                    name="expected_warranty"
                    defaultValue={vehicle?.expected_warranty_type === "own" ? `own:${vehicle.warranty_months ?? expectedWarrantySettings?.own_default_months ?? 18}` : vehicle?.expected_warranty_product_id ? `product:${vehicle.expected_warranty_product_id}` : ""}
                    disabled={!writable}
                  >
                    <option value="">Sem garantia definida</option>
                    {expectedWarrantySettings && ["own", "mixed"].includes(expectedWarrantySettings.mode) && (
                      <option value={`own:${expectedWarrantySettings.own_default_months}`}>Garantia própria · {expectedWarrantySettings.own_default_months} meses</option>
                    )}
                    {expectedWarrantyProducts.filter((product) => product.active && expectedWarrantyProviders.some((provider) => provider.id === product.provider_id && provider.active)).map((product) => {
                      const provider = expectedWarrantyProviders.find((item) => item.id === product.provider_id);
                      return <option key={product.id} value={`product:${product.id}`}>{product.name} · {product.duration_months} meses · {provider?.name}</option>;
                    })}
                  </select>
                  <small>Opções disponíveis em Configurações → Garantias.</small>
                </label>
                <label className="wide">
                  <span>Notas internas</span>
                  <textarea
                    name="internal_notes"
                    rows={3}
                    defaultValue={vehicle?.internal_notes ?? ""}
                    disabled={!writable}
                  />
                </label>
              </div>
            </fieldset>
            <fieldset className="stock-section">
              <legend>Preço e publicação</legend>
              <p className="stock-section-help">Condições comerciais e fiscais sincronizadas com o Financeiro para calcular IVA, custos previstos e margem líquida.</p>
              <div className="stock-form-grid">
                <label>
                  <span>Tipo de negócio</span>
                  <select key={`business-${businessTerms?.business_type ?? "used"}`} name="business_type" defaultValue={businessTerms?.business_type ?? "used"} disabled={!writable || !canManageFinance}>
                    <option value="new">Novo</option><option value="used">Usado</option><option value="demonstration">Demonstração</option><option value="service">Serviço</option>
                  </select>
                </label>
                <label>
                  <span>Regime do IVA *</span>
                  <select name="vat_regime" value={vatRegime} onChange={(event)=>setVatRegime(event.target.value)} disabled={!writable || !canManageFinance}>
                    <option value="margin_scheme">Regime da margem</option><option value="standard_vat">IVA normal</option><option value="vat_exempt">Isento de IVA</option><option value="intra_community">Intracomunitário</option><option value="import">Importação</option><option value="other">Outro</option>
                  </select>
                  <small>{vatRegimeHelp[vatRegime]}</small>
                </label>
                <label>
                  <span>Taxa de IVA aplicada *</span>
                  <select value={vatRatePreset} onChange={(event)=>setVatRatePreset(event.target.value)} disabled={!writable || !canManageFinance}>
                    <option value="23">23%</option><option value="22">22%</option><option value="16">16%</option><option value="13">13%</option><option value="12">12%</option><option value="9">9%</option><option value="6">6%</option><option value="5">5%</option><option value="0">0%</option><option value="custom">Outra taxa</option>
                  </select>
                </label>
                <label>
                  <span>Taxa personalizada</span>
                  <div className="stock-percent-input"><input type="number" min="0" max="100" step="0.01" inputMode="decimal" value={customVatRate} onChange={(event)=>setCustomVatRate(event.target.value)} disabled={!writable || !canManageFinance || vatRatePreset!=="custom"}/><em>%</em></div>
                </label>
                <label>
                  <span>Disponível para venda</span>
                  <input key={`available-${businessTerms?.available_for_sale_on ?? ""}`} name="available_for_sale_on" type="date" defaultValue={businessTerms?.available_for_sale_on ?? ""} disabled={!writable || !canManageFinance}/>
                </label>
                {countryCode === "PT" && <label>
                  <span>Valor do IUC</span>
                  <CurrencyInput currencyCode={currencyCode} name="iuc_amount" type="text" inputMode="decimal" value={iucAmountInput} onChange={(event)=>setIucAmountInput(event.target.value)} disabled={!writable || !canManageFinance} placeholder="Ex.: 180"/>
                  <small>Entra automaticamente como custo fiscal previsto da viatura.</small>
                </label>}
                <label>
                  <span>Preço de compra</span>
                  <CurrencyInput
                      currencyCode={currencyCode}
                      name="purchase_price"
                      type="text"
                      inputMode="decimal"
                      value={purchasePriceInput}
                      onChange={(event) => setPurchasePriceInput(event.target.value)}
                      disabled={!writable}
                      placeholder="Ex.: 14.500"
                    />
                  <small>
                    {parseCurrencyInputToCents(purchasePriceInput) === null
                      ? `Valor total pago pela viatura. Exemplo: ${currencyCode === "BRL" ? "R$ 14.500" : "14.500 €"}.`
                      : `Será guardado como ${currencyCode === "BRL" ? "R$ " : ""}${formatCurrencyInput(parseCurrencyInputToCents(purchasePriceInput))}${currencyCode === "BRL" ? "" : " €"}.`}
                  </small>
                </label>
                <label>
                  <span>Preço de venda</span>
                  <CurrencyInput
                      currencyCode={currencyCode}
                      name="sale_price"
                      type="text"
                      inputMode="decimal"
                      value={salePriceInput}
                      onChange={(event) => setSalePriceInput(event.target.value)}
                      disabled={!writable}
                      placeholder="Ex.: 19.100"
                    />
                  <small>
                    {parseCurrencyInputToCents(salePriceInput) === null
                      ? `Preço total anunciado ao cliente. Exemplo: ${currencyCode === "BRL" ? "R$ 19.100" : "19.100 €"}.`
                      : `Será guardado como ${currencyCode === "BRL" ? "R$ " : ""}${formatCurrencyInput(parseCurrencyInputToCents(salePriceInput))}${currencyCode === "BRL" ? "" : " €"}.`}
                  </small>
                </label>
                <div className="stock-business-options wide">
                  <label className="stock-option"><input key={`itemized-${businessTerms?.vat_itemized ?? true}`} name="vat_itemized" type="checkbox" defaultChecked={businessTerms?.vat_itemized ?? true} disabled={!writable || !canManageFinance}/><span><strong>IVA discriminado</strong><small>O IVA aparece separado do preço nos documentos e na informação comercial.</small></span></label>
                  <label className="stock-option"><input key={`deductible-${businessTerms?.vat_deductible ?? false}`} name="vat_deductible" type="checkbox" defaultChecked={businessTerms?.vat_deductible ?? false} disabled={!writable || !canManageFinance}/><span><strong>IVA dedutível</strong><small>Desconta o IVA da compra na estimativa, apenas quando a fatura e a lei o permitem.</small></span></label>
                  <label className="stock-option"><input key={`fixed-${businessTerms?.fixed_price ?? false}`} name="fixed_price" type="checkbox" defaultChecked={businessTerms?.fixed_price ?? false} disabled={!writable || !canManageFinance}/><span><strong>Valor fixo</strong><small>Indica que o preço anunciado não é negociável.</small></span></label>
                  <label className="stock-option"><input key={`exchange-${businessTerms?.accepts_exchange ?? false}`} name="accepts_exchange" type="checkbox" defaultChecked={businessTerms?.accepts_exchange ?? false} disabled={!writable || !canManageFinance}/><span><strong>Aceita troca</strong><small>Permite negociar esta viatura diretamente por outra.</small></span></label>
                  <label className="stock-option"><input key={`trade-${businessTerms?.accepts_trade_in ?? true}`} name="accepts_trade_in" type="checkbox" defaultChecked={businessTerms?.accepts_trade_in ?? true} disabled={!writable || !canManageFinance}/><span><strong>Aceita retoma</strong><small>O cliente pode entregar outra viatura como parte do pagamento.</small></span></label>
                  <label className="stock-option"><input key={`isv-${businessTerms?.price_excludes_isv ?? false}`} name="price_excludes_isv" type="checkbox" defaultChecked={businessTerms?.price_excludes_isv ?? false} disabled={!writable || !canManageFinance}/><span><strong>Valor sem ISV</strong><small>O preço ainda não inclui ISV; o imposto confirmado deve ser lançado antes da venda.</small></span></label>
                  <label className="stock-option"><input key={`financing-${businessTerms?.financing_available ?? true}`} name="financing_available" type="checkbox" defaultChecked={businessTerms?.financing_available ?? true} disabled={!writable || !canManageFinance}/><span><strong>Possibilidade de financiamento</strong><small>Permite apresentar opções de crédito ao comprador.</small></span></label>
                  <label className="stock-option"><input name="featured" type="checkbox" defaultChecked={vehicle?.featured} disabled={!writable}/><span><strong>Destacar esta viatura</strong><small>Dá maior visibilidade à viatura no site público do stand.</small></span></label>
                </div>
                {businessTerms && <div className="stock-tax-preview wide" role="status"><span>IVA estimado</span><strong>{new Intl.NumberFormat("pt-PT",{style:"currency",currency:currencyCode}).format(businessTerms.estimated_vat_cents/100)}</strong><small>Será recalculado ao guardar e refletido na margem líquida do Financeiro.</small></div>}
                <label className="wide">
                  <span>Descrição</span>
                  <textarea
                    name="description"
                    rows={4}
                    defaultValue={vehicle?.description ?? ""}
                    disabled={!writable}
                  />
                </label>
              </div>
            </fieldset>
          </div>
          <div className="vehicle-profile-panel" hidden={profileTab !== "consignment"}>
            {agreement ? (
              <fieldset className="stock-section consignment-summary">
                <legend>Consignação</legend>
                <p className="stock-section-help">Acordo associado a esta viatura e ao perfil do cliente proprietário.</p>
                <div className="consignment-summary-grid">
                  <article><small>Cliente proprietário</small><strong>{owners.find((owner) => owner.id === ownerId)?.display_name ?? "Cliente associado"}</strong></article>
                  <article><small>Estado</small><strong>{agreement.status === "active" ? "Ativo" : agreement.status === "draft" ? "Rascunho" : agreement.status === "ended" ? "Terminado" : "Cancelado"}</strong></article>
                  <article><small>Valor devido</small><strong>{new Intl.NumberFormat("pt-PT", { style: "currency", currency: currencyCode }).format(agreement.owner_amount_cents / 100)}</strong></article>
                  <article><small>Remuneração do stand</small><strong>{agreement.commission_type === "margin" ? "Margem entre o valor devido e a venda" : agreement.commission_type === "fixed" ? `Comissão fixa de ${new Intl.NumberFormat("pt-PT", { style: "currency", currency: currencyCode }).format(Number(agreement.commission_value ?? 0))}` : `Comissão de ${Number(agreement.commission_value ?? 0).toLocaleString("pt-PT")}% sobre a venda`}</strong></article>
                  <article><small>Preço mínimo autorizado</small><strong>{agreement.minimum_sale_price_cents == null ? "Não definido" : new Intl.NumberFormat("pt-PT", { style: "currency", currency: currencyCode }).format(agreement.minimum_sale_price_cents / 100)}</strong></article>
                  <article><small>Período</small><strong>{agreement.starts_on}{agreement.ends_on ? ` — ${agreement.ends_on}` : " — sem fim definido"}</strong></article>
                  <article className="wide"><small>Contacto do proprietário</small><strong>{(() => { const owner = owners.find((item) => item.id === ownerId); return [owner?.phone, owner?.email].filter(Boolean).join(" · ") || "Sem telefone ou email registado."; })()}</strong></article>
                  <article className="wide"><small>Condições acordadas</small><strong>{agreement.terms || "Sem condições adicionais registadas."}</strong></article>
                  <article className="wide"><small>Notas internas</small><strong>{agreement.internal_notes || "Sem notas internas."}</strong></article>
                </div>
                {writable && canManageLifecycle && <button type="button" className="stock-primary consignment-edit" onClick={() => setConsignmentDialogOpen(true)}>Editar consignação</button>}
              </fieldset>
            ) : (
              <fieldset className="stock-section consignment-summary consignment-summary-empty">
                <legend>Consignação</legend>
                <p className="stock-section-help">Esta viatura está marcada como consignada, mas o acordo ainda não foi guardado. Preencha o formulário para associar o proprietário e tornar as condições acessíveis nesta aba.</p>
                {writable && canManageLifecycle && <button type="button" className="stock-primary consignment-edit" onClick={() => setConsignmentDialogOpen(true)}>Preencher consignação</button>}
              </fieldset>
            )}
          </div>
          {error && <p className="stock-error">{error}</p>}
          {profileTab !== "chronology" &&
            profileTab !== "commercial" &&
            profileTab !== "marketing" &&
            profileTab !== "media" && (
            <footer>
              <button
                type="button"
                className="stock-secondary"
                onClick={() => void closeDialog()}
              >
                {writable ? "Cancelar" : "Fechar"}
              </button>
              {writable && (
                <button className="stock-primary" disabled={saving}>
                  {saving ? "A guardar…" : "Guardar"}
                </button>
              )}
            </footer>
          )}
        </form>
        {currentVehicle?.creation_status === "active" &&
          profileTab === "marketing" && (
            <VehicleMarketingPanel
              vehicleId={currentVehicle.id}
              organizationId={organizationId}
              writable={writable && canManageLifecycle}
            />
          )}
        {currentVehicle?.creation_status === "active" &&
          profileTab === "commercial" && (
            <VehicleCommercialPanel
              vehicleId={currentVehicle.id}
              organizationId={organizationId}
              writable={writable}
              canSupervise={roleKey === "stand_master" || roleKey === "manager"}
            />
          )}
        {currentVehicle?.creation_status === "active" &&
          profileTab === "chronology" && (
            <div className="vehicle-history-workspace">
              <VehicleChronologyPanel
                vehicleId={currentVehicle.id}
                organizationId={organizationId}
                writable={writable}
                canManageFinance={canManageFinance}
                currencyCode={currencyCode}
              />
              <fieldset className="stock-section vehicle-history">
                <legend>Histórico de alterações</legend>
                <p className="stock-section-help">Histórico da viatura: registo cronológico das alterações feitas à ficha e ao respetivo estado.</p>
                {history.length === 0 ? <p className="stock-section-help">Ainda não existem alterações registadas.</p> : history.map((item) => {
                  const data = item.after_data ?? item.before_data ?? {};
                  const reason = String(data.lifecycle_note ?? data.pause_reason ?? data.reason ?? "");
                  return <article key={item.id}><div><strong>{item.event_type === "created" ? "Viatura criada" : item.event_type === "status_changed" ? "Estado alterado" : item.event_type === "sale_started" ? "Venda iniciada" : item.event_type === "sale_reversed" ? "Venda revertida" : "Ficha atualizada"}</strong>{reason && <span>{reason}</span>}</div><time>{new Date(item.created_at).toLocaleString(currencyCode === "BRL" ? "pt-BR" : "pt-PT")}</time></article>;
                })}
              </fieldset>
            </div>
          )}
      </section>
    </div>
  );
}

function ReservationPanel({
  vehicle,
  organizationId,
  currencyCode,
  writable,
  roleKey,
  canManageLifecycle,
  onChanged,
}: {
  vehicle: Vehicle;
  organizationId: string;
  currencyCode: string;
  writable: boolean;
  roleKey: RoleKey;
  canManageLifecycle: boolean;
  onChanged?: () => void | Promise<void>;
}) {
  const [reservation, setReservation] = useState<VehicleReservation | null>(
    null,
  );
  const [requests, setRequests] = useState<ReservationRequest[]>([]);
  const [userId, setUserId] = useState("");
  const [names, setNames] = useState<Record<string, string>>({});
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [deposit, setDeposit] = useState("");
  const [notes, setNotes] = useState("");
  const [requestCustomer, setRequestCustomer] = useState("");
  const [requestMessage, setRequestMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const manager =
    canManageLifecycle || roleKey === "stand_master" || roleKey === "manager";
  const load = async () => {
    const [reservationResult, userResult] = await Promise.all([
      supabase
        .from("vehicle_reservations")
        .select(
          "id,customer_name,customer_email,customer_phone,assigned_user_id,status,expires_at,deposit_cents,currency_code,notes",
        )
        .eq("vehicle_id", vehicle.id)
        .eq("status", "active")
        .maybeSingle(),
      supabase.auth.getUser(),
    ]);
    const current = reservationResult.data as VehicleReservation | null;
    setReservation(current);
    setUserId(userResult.data.user?.id ?? "");
    if (!current) {
      setRequests([]);
      return;
    }
    const requestResult = await supabase
      .from("vehicle_reservation_requests")
      .select(
        "id,requested_by,proposed_customer_name,message,status,created_at",
      )
      .eq("reservation_id", current.id)
      .order("created_at", { ascending: false });
    const rows = (requestResult.data ?? []) as ReservationRequest[];
    setRequests(rows);
    const ids = [
      ...new Set([
        current.assigned_user_id,
        ...rows.map((item) => item.requested_by),
      ]),
    ];
    if (ids.length) {
      const profileResult = await supabase
        .from("profiles")
        .select("user_id,display_name")
        .in("user_id", ids);
      setNames(
        Object.fromEntries(
          (profileResult.data ?? []).map((profile) => [
            profile.user_id,
            profile.display_name ?? "Utilizador",
          ]),
        ),
      );
    }
  };
  useEffect(() => {
    void load();
  }, [vehicle.id]);
  useEffect(() => {
    if (!feedback) return;
    const timer = setTimeout(() => setFeedback(null), 10000);
    return () => clearTimeout(timer);
  }, [feedback]);
  const createReservation = async () => {
    if (!customerName.trim() || !expiresAt) {
      setFeedback("Indique o cliente e a validade da reserva.");
      return;
    }
    setBusy(true);
    const { error } = await supabase.rpc("create_vehicle_reservation", {
      p_vehicle_id: vehicle.id,
      p_customer_name: customerName.trim(),
      p_customer_email: customerEmail.trim(),
      p_customer_phone: customerPhone.trim(),
      p_expires_at: new Date(expiresAt).toISOString(),
      p_deposit_cents: deposit ? Math.round(Number(deposit) * 100) : null,
      p_notes: notes.trim(),
    });
    setBusy(false);
    setFeedback(
      error
        ? error.message
        : "Reserva criada. A viatura ficou bloqueada para este cliente.",
    );
    if (!error) { await load(); await onChanged?.(); }
  };
  const endReservation = async () => {
    if (!reservation) return;
    const reason = window.prompt(
      "Indique o motivo para libertar esta reserva:",
    );
    if (!reason?.trim()) return;
    setBusy(true);
    const { error } = await supabase.rpc("end_vehicle_reservation", {
      p_reservation_id: reservation.id,
      p_action: "released",
      p_reason: reason.trim(),
    });
    setBusy(false);
    setFeedback(
      error ? error.message : "Reserva libertada e registada no histórico.",
    );
    if (!error) { await load(); await onChanged?.(); }
  };
  const requestRelease = async () => {
    if (!reservation || !requestCustomer.trim() || !requestMessage.trim()) {
      setFeedback(
        "Indique o seu cliente e explique o pedido ao comercial responsável.",
      );
      return;
    }
    setBusy(true);
    const { error } = await supabase.rpc(
      "request_vehicle_reservation_release",
      {
        p_reservation_id: reservation.id,
        p_customer_name: requestCustomer.trim(),
        p_message: requestMessage.trim(),
      },
    );
    setBusy(false);
    setFeedback(
      error ? error.message : "Pedido enviado ao responsável pela reserva.",
    );
    if (!error) { await load(); await onChanged?.(); }
  };
  const decide = async (
    requestId: string,
    decision: "accepted" | "refused" | "overridden",
  ) => {
    const message = window.prompt(
      decision === "refused"
        ? "Explique por que mantém a reserva:"
        : "Mensagem para o comercial que receberá a reserva:",
    );
    if (message === null) return;
    setBusy(true);
    const { error } = await supabase.rpc("decide_vehicle_reservation_request", {
      p_request_id: requestId,
      p_decision: decision,
      p_message: message.trim(),
    });
    setBusy(false);
    setFeedback(
      error
        ? error.message
        : decision === "refused"
          ? "Pedido recusado."
          : "Reserva transferida com histórico preservado.",
    );
    if (!error) { await load(); await onChanged?.(); }
  };
  const isOwner = reservation?.assigned_user_id === userId;
  return (
    <section className="stock-section reservation-section">
      <h3>Reserva comercial</h3>
      <p className="stock-section-help">
        Uma reserva bloqueia a viatura para o cliente e comercial indicados.
        Outro comercial deve pedir a libertação; Gestor e Master podem intervir.
      </p>
      {feedback && (
        <button
          type="button"
          className="stock-notice"
          onClick={() => setFeedback(null)}
        >
          {feedback} ×
        </button>
      )}
      {!reservation ? (
        <div className="stock-form-grid">
          <label>
            <span>Cliente *</span>
            <input
              value={customerName}
              onChange={(event) => setCustomerName(event.target.value)}
              disabled={!writable || busy}
              placeholder="Nome do cliente interessado"
            />
          </label>
          <label>
            <span>Validade da reserva *</span>
            <input
              type="datetime-local"
              value={expiresAt}
              onChange={(event) => setExpiresAt(event.target.value)}
              disabled={!writable || busy}
            />
          </label>
          <label>
            <span>Telefone</span>
            <input
              value={customerPhone}
              onChange={(event) => setCustomerPhone(event.target.value)}
              disabled={!writable || busy}
            />
          </label>
          <label>
            <span>Email</span>
            <input
              type="email"
              value={customerEmail}
              onChange={(event) => setCustomerEmail(event.target.value)}
              disabled={!writable || busy}
            />
          </label>
          <label>
            <span>Sinal ({currencyCode})</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={deposit}
              onChange={(event) => setDeposit(event.target.value)}
              disabled={!writable || busy}
            />
          </label>
          <label>
            <span>Notas</span>
            <input
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              disabled={!writable || busy}
            />
          </label>
          {writable && (
            <div className="wide reservation-actions">
              <button
                type="button"
                className="stock-secondary"
                onClick={createReservation}
                disabled={busy}
              >
                {busy ? "A registar…" : "Reservar viatura"}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="reservation-active">
          <div className="reservation-summary">
            <div>
              <small>Cliente</small>
              <strong>{reservation.customer_name}</strong>
              <span>
                {reservation.customer_phone ||
                  reservation.customer_email ||
                  "Contacto não indicado"}
              </span>
            </div>
            <div>
              <small>Responsável</small>
              <strong>
                {names[reservation.assigned_user_id] ?? "Comercial"}
              </strong>
              <span>
                Até {new Date(reservation.expires_at).toLocaleString("pt-PT")}
              </span>
            </div>
            <div>
              <small>Sinal</small>
              <strong>
                {money(reservation.deposit_cents, reservation.currency_code)}
              </strong>
              <span>{reservation.notes || "Sem notas"}</span>
            </div>
          </div>
          {(isOwner || manager) && writable && (
            <div className="reservation-actions">
              <button
                type="button"
                className="stock-secondary"
                onClick={endReservation}
                disabled={busy}
              >
                Libertar reserva
              </button>
            </div>
          )}
          {!isOwner && writable && (
            <div className="reservation-request-form">
              <strong>Tem outro cliente interessado?</strong>
              <input
                value={requestCustomer}
                onChange={(event) => setRequestCustomer(event.target.value)}
                placeholder="Nome do seu cliente"
                disabled={busy}
              />
              <textarea
                value={requestMessage}
                onChange={(event) => setRequestMessage(event.target.value)}
                placeholder="Explique o pedido de libertação ao responsável"
                rows={2}
                disabled={busy}
              />
              <button
                type="button"
                className="stock-secondary"
                onClick={requestRelease}
                disabled={busy}
              >
                Pedir libertação
              </button>
            </div>
          )}
          {requests.length > 0 && (
            <div className="reservation-requests">
              <h3>Pedidos de libertação</h3>
              {requests.map((request) => (
                <article key={request.id}>
                  <div>
                    <strong>{request.proposed_customer_name}</strong>
                    <span>
                      {names[request.requested_by] ?? "Comercial"} ·{" "}
                      {request.message}
                    </span>
                    <small>
                      {request.status === "pending"
                        ? "A aguardar decisão"
                        : `Estado: ${request.status}`}
                    </small>
                  </div>
                  {request.status === "pending" && (isOwner || manager) && (
                    <div className="reservation-actions">
                      <button
                        type="button"
                        onClick={() => decide(request.id, "accepted")}
                        disabled={busy}
                      >
                        Transferir
                      </button>
                      <button
                        type="button"
                        onClick={() => decide(request.id, "refused")}
                        disabled={busy}
                      >
                        Manter
                      </button>
                      {manager && !isOwner && (
                        <button
                          type="button"
                          onClick={() => decide(request.id, "overridden")}
                          disabled={busy}
                        >
                          Decidir como gestor
                        </button>
                      )}
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
